import React, { useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Button, Input, Label, ModalBody } from "reactstrap";
import {
  AppModal as Modal,
  AppOffcanvas as Offcanvas,
} from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import { startGuidedTour } from "../../components/Help/GuidedTour";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import {
  createReviewTemplateDraftVersion,
  duplicateReviewTemplate,
  getApplicationRoles,
  getChecklists,
  getClientTypes,
  getIndustryTypes,
  getReportTemplates,
  getReviewTemplateConfiguration,
  getReviewTemplateVersionList,
  getWorkTypes,
  getReviewTypes,
  publishReviewTemplate,
  saveReviewTemplateConfiguration,
} from "../../helpers/backend_helper";
import ChecklistPreview from "../Checklists/ChecklistPreview";
import { postRawData } from "../../helpers/url-request";
import { normalizeTags } from "./templateTags";
import "./ReviewTemplateStudio.scss";

const buildOutline = (
  stages,
  checklistSections,
  reportSections,
  references,
  documentFolders
) => [
  ["information", "bx-info-circle", "Information"],
  [
    "checklists",
    "bx-list-check",
    "Checklists",
    checklistSections.length ? String(checklistSections.length) : undefined,
  ],
  [
    "reports",
    "bx-file-blank",
    "Report Formats",
    reportSections.reduce(
      (total, section) => total + section.reportIds.length,
      0
    )
      ? String(
          reportSections.reduce(
            (total, section) => total + section.reportIds.length,
            0
          )
        )
      : undefined,
  ],
  ["findings", "bx-flag", "Findings", "6"],
  [
    "documents",
    "bx-folder",
    "Documents & Workpapers",
    documentFolders.length ? String(documentFolders.length) : undefined,
  ],
  ["workflow", "bx-git-branch", "Workflow", String(stages.length)],
  [
    "references",
    "bx-link",
    "References",
    references.length ? String(references.length) : undefined,
  ],
  ["settings", "bx-cog", "Settings"],
];

const orderDocumentFolders = (folders) => {
  const foldersByPath = new Map(folders.map((folder) => [folder.path, folder]));

  const getSortKey = (folder) =>
    folder.path
      .split("/")
      .map((_, index, segments) => {
        const ancestorPath = segments.slice(0, index + 1).join("/");
        return String(
          foldersByPath.get(ancestorPath)?.sortOrder ?? Number.MAX_SAFE_INTEGER
        ).padStart(12, "0");
      })
      .join(".");

  return [...folders].sort((left, right) =>
    getSortKey(left).localeCompare(getSortKey(right))
  );
};

const informationGuidance = [
  [
    "about",
    "bx-info-circle",
    "About this section",
    "Add the core template details. Fields marked with an asterisk are required before publishing.",
  ],
  [
    "preview",
    "bx-show",
    "Preview before publishing",
    "Use Preview to check the reviewer experience before making the template available.",
  ],
];
const guidanceByStep = {
  information: informationGuidance,
  checklists: [
    [
      "checklist-structure",
      "bx-list-check",
      "Organize checklists",
      "Create sections to group the checklists used in this review template.",
    ],
    [
      "checklist-library",
      "bx-link-external",
      "Link existing checklists",
      "Choose approved checklists from the library, then arrange them in the appropriate section.",
    ],
  ],
  reports: [
    [
      "report-formats",
      "bx-file-blank",
      "Configure report formats",
      "Link the reports this template can generate and set their availability.",
    ],
    [
      "report-timing",
      "bx-time-five",
      "Set report timing",
      "Choose when each report becomes available in the review workflow.",
    ],
  ],
  findings: [
    [
      "finding-categories",
      "bx-flag",
      "Define finding categories",
      "Set up the categories reviewers use to classify findings.",
    ],
    [
      "finding-severity",
      "bx-error-circle",
      "Review severity levels",
      "Keep severity labels clear so findings can be prioritized consistently.",
    ],
  ],
  documents: [
    [
      "system-folders",
      "bx-lock-alt",
      "System workpapers",
      "These protected folders hold review workpapers and generated reports.",
    ],
    [
      "client-folders",
      "bx-folder-plus",
      "Client documents",
      "Engagement users can add folders only within Client Documents.",
    ],
  ],
  workflow: [
    [
      "workflow-roles",
      "bx-git-branch",
      "Configure workflow roles",
      "Set reviewer, sign-off, and final approval responsibilities for this template.",
    ],
    [
      "workflow-gates",
      "bx-check-shield",
      "Review completion gates",
      "Confirm the readiness and completion checks required before the review can progress.",
    ],
  ],
  references: [
    [
      "reference-library",
      "bx-link",
      "Add references",
      "Link the standards, policies, and supporting resources reviewers need.",
    ],
    [
      "reference-version",
      "bx-revision",
      "Keep references current",
      "Record the applicable version so teams use the right source material.",
    ],
  ],
  settings: [
    [
      "template-behavior",
      "bx-cog",
      "Configure template behavior",
      "Set the review options that control how this template behaves during an engagement.",
    ],
    [
      "settings-review",
      "bx-check-circle",
      "Review before publishing",
      "Confirm these settings support the intended review process before publishing the template.",
    ],
  ],
};
const defaultStages = [
  {
    name: "Draft",
    description: "Template is being prepared.",
    role: "Template Owner",
    tone: "violet",
    stageType: "manual",
  },
  {
    name: "Reviewer",
    description: "Reviewer performs detailed review.",
    role: "Reviewer",
    tone: "blue",
    stageType: "manual",
  },
  {
    name: "Manager Review",
    description: "Manager reviews the work.",
    role: "Manager",
    tone: "orange",
    stageType: "manual",
  },
  {
    name: "Partner Review",
    description: "Partner performs final review.",
    role: "Partner",
    tone: "purple",
    stageType: "manual",
  },
  {
    name: "Completed",
    description: "Review is completed.",
    role: "System",
    tone: "green",
    stageType: "system",
  },
  {
    name: "Locked",
    description: "Review is locked and no further changes allowed.",
    role: "System",
    tone: "gray",
    stageType: "system",
  },
];
const stageRoles = [
  "Template Owner",
  "Reviewer",
  "Manager",
  "Partner",
  "QC Reviewer",
  "System",
];
const fixedLifecycle = [
  [
    "Engagement Preparation",
    "configurable",
    "Confirm readiness and review team assignments.",
  ],
  [
    "Ready for Review",
    "system",
    "System gate once readiness requirements are met.",
  ],
  [
    "Checklist Review",
    "configurable",
    "Review linked checklists and resolve required work.",
  ],
  [
    "Checklist Sign-off",
    "configurable",
    "Approve or return each completed checklist.",
  ],
  [
    "Review Completion",
    "system",
    "System validation of required checklists, findings, and reports.",
  ],
  [
    "Final Engagement Sign-off",
    "configurable",
    "Sequential engagement-level approval.",
  ],
  ["Completed", "final", "System-controlled final state."],
];
const defaultWorkflowConfiguration = {
  engagementPreparationResponsibleRoleId: null,
  engagementPreparationResponsibleRoleName: "",
  additionalAuthorizedRoleIds: [],
  readinessRequirements: {
    engagementInformationComplete: true,
    reviewTeamAssigned: true,
    requiredRolesAssigned: true,
    requiredChecklistsAvailable: true,
    requiredEngagementDataAvailable: true,
    requiredDocumentsAvailable: false,
  },
  checklistAssignments: [],
  finalSignOffs: [
    { role: "Review Manager", required: true },
    { role: "Engagement Partner", required: true },
  ],
};
const readinessRequirementOptions = [
  ["engagementInformationComplete", "Engagement information complete"],
  ["reviewTeamAssigned", "Review team assigned"],
  ["requiredRolesAssigned", "Required roles assigned"],
  ["requiredChecklistsAvailable", "Required checklists available"],
  ["requiredEngagementDataAvailable", "Required engagement data available"],
  ["requiredDocumentsAvailable", "Required documents/workpapers available"],
];

const WorkflowTimelineStage = ({
  number,
  tone,
  icon,
  title,
  label,
  description,
  action,
  children,
  isLast = false,
}) => (
  <section
    className={`piers-workflow__timeline-stage is-${tone}${
      isLast ? " is-last" : ""
    }`}
  >
    <span className="piers-workflow__timeline-number">
      {isLast ? <i className="bx bx-check" /> : String(number).padStart(2, "0")}
    </span>
    <span className="piers-workflow__timeline-rail" aria-hidden="true" />
    <span className="piers-workflow__timeline-icon">
      <i className={`bx ${icon}`} />
    </span>
    <div className="piers-workflow__timeline-content">
      <header>
        <div>
          <h3>{title}</h3>
          <b>{label}</b>
          <p>{description}</p>
        </div>
        {action}
      </header>
      {children}
    </div>
  </section>
);
const blankStage = {
  name: "",
  description: "",
  role: "Template Owner",
  stageType: "manual",
  optional: false,
};
const stageTones = ["blue", "violet", "orange", "purple", "green"];
const checklistLibrary = [
  {
    id: "general-audit",
    name: "AICPA General Audit Engagement Checklist",
    standard: "AICPA",
    workType: "Review, Audit",
    questions: 126,
  },
  {
    id: "income-tax",
    name: "AICPA - 2025 Individual Income Tax Return",
    standard: "AICPA",
    workType: "Review, Compilation",
    questions: 383,
  },
  {
    id: "year-end",
    name: "Practitioner Year-End Planning Checklist",
    standard: "AICPA",
    workType: "Review, Compilation",
    questions: 68,
  },
  {
    id: "demo",
    name: "AICPA Demo Checklist",
    standard: "AICPA",
    workType: "Review",
    questions: 25,
  },
  {
    id: "completion-review",
    name: "Compilation Review Checklist",
    standard: "AICPA",
    workType: "Review, Compilation",
    questions: 60,
  },
  {
    id: "independence",
    name: "Independence Review Checklist",
    standard: "AICPA",
    workType: "Review, Audit",
    questions: 45,
  },
  {
    id: "engagement-planning",
    name: "Engagement Planning Checklist",
    standard: "AICPA",
    workType: "Review, Audit",
    questions: 38,
  },
  {
    id: "audit-execution",
    name: "Audit Execution Checklist",
    standard: "AICPA",
    workType: "Review, Audit",
    questions: 210,
  },
  {
    id: "documentation",
    name: "Documentation Review Checklist",
    standard: "AICPA",
    workType: "Review, Audit",
    questions: 72,
  },
  {
    id: "completion",
    name: "Completion Checklist",
    standard: "AICPA",
    workType: "Review, Audit",
    questions: 55,
  },
  {
    id: "partner-review",
    name: "Partner Review Checklist",
    standard: "AICPA",
    workType: "Review, Audit",
    questions: 28,
  },
];
const initialChecklistSections = [];
const reportLibrary = [
  {
    id: "management-letter",
    name: "Peer Review Management Letter",
    category: "Final Report",
    formatType: "Word Template",
    updated: "Updated 2 weeks ago",
  },
  {
    id: "peer-review-final",
    name: "Peer Review Final Report",
    category: "Final Report",
    formatType: "Word Template",
    updated: "Updated 2 weeks ago",
  },
  {
    id: "findings-summary",
    name: "Findings Summary Report",
    category: "Findings Report",
    formatType: "Word Template",
    updated: "Updated 1 week ago",
  },
  {
    id: "detailed-findings",
    name: "Detailed Findings Report",
    category: "Findings Report",
    formatType: "Word Template",
    updated: "Updated 3 weeks ago",
  },
  {
    id: "checklist-summary",
    name: "Checklist Summary Report",
    category: "Checklist Report",
    formatType: "Word Template",
    updated: "Updated 3 weeks ago",
  },
];
const initialReportSections = [];
const defaultReportAvailability = {
  scope: "engagement",
  generateAt: "manual",
  finalizeAt: "",
  appliesTo: "all",
  checklistIds: [],
  allowManualRegeneration: false,
  trigger: "manual",
  stage: "",
  roles: ["Manager"],
  approvedFindingsOnly: false,
  completedChecklistsOnly: false,
  includeReferences: false,
};

const defaultFindingCategories = [
  "Documentation",
  "Independence",
  "Quality Management",
  "Engagement Performance",
  "Professional Standards",
  "Other",
];
const defaultSeverityLevels = [
  { name: "Critical", detail: "Highest risk / impact", tone: "critical" },
  { name: "High", detail: "High risk / significant impact", tone: "high" },
  { name: "Medium", detail: "Moderate risk / impact", tone: "medium" },
  { name: "Low", detail: "Low risk / minor impact", tone: "low" },
];
const blankSeverityLevel = {
  name: "",
  detail: "",
  tone: "low",
  originalName: "",
};
const initialReferences = [];
const blankReference = {
  title: "",
  type: "",
  version: "",
  source: "external",
  value: "",
  description: "",
};
const defaultTemplateSettings = {
  mandatoryQuestions: true,
  allowNa: true,
  completionComments: false,
  lockAfterCompletion: true,
  additionalChecklists: false,
  skipOptionalChecklists: true,
  namingFormat: "{Client} - {Year} Review",
};

const formatPreviewDate = (value) => {
  if (!value) return "Not yet updated";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? "Not yet updated"
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const formatPreviewVersion = (version) => {
  if (!version) return "Not configured";
  if (version.versionLabel) return version.versionLabel.replace(/^v/i, "");
  return version.versionNumber === undefined || version.versionNumber === null
    ? "Not configured"
    : `${version.versionNumber}.0`;
};

const applicabilitySelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1100 }),
};

const ReviewTemplateStudio = ({
  template: suppliedTemplate,
  closeAction,
  initialPreview = false,
  initialHistory = false,
  previewOnly = false,
  onEditTemplate,
  onDuplicateTemplate,
  onCreated,
}) => {
  const [createdTemplate, setCreatedTemplate] = useState(null);
  const template = suppliedTemplate || createdTemplate;
  const createdTemplateRef = React.useRef(null);
  const [active, setActive] = useState("information");
  const [activePreviewSection, setActivePreviewSection] = useState(
    "preview-information"
  );
  const [expandedGuidance, setExpandedGuidance] = useState("about");
  const activeStepGuidance = guidanceByStep[active] || informationGuidance;
  const displayedGuidanceId = activeStepGuidance.some(
    ([id]) => id === expandedGuidance
  )
    ? expandedGuidance
    : activeStepGuidance[0]?.[0];
  const [templateInformation, setTemplateInformation] = useState(() => ({
    name: template?.name || "",
    code: "",
    description: template?.description || "",
    owner: "Quality Management Team",
    effectiveDate: "Jan 01, 2026",
    version: "1.0",
    status: "Draft",
    tags: [],
  }));
  const [autoSaveStatus, setAutoSaveStatus] = useState("saved");
  const [tagInput, setTagInput] = useState("");
  const [informationSubmitted, setInformationSubmitted] = useState(false);
  const [isManualSavePending, setIsManualSavePending] = useState(false);
  const [isTemplatePublished, setIsTemplatePublished] = useState(
    Boolean(template?.currentVersion?.isPublished)
  );
  const [currentTemplateVersion, setCurrentTemplateVersion] = useState(
    template?.currentVersion || null
  );
  const [templateVersions, setTemplateVersions] = useState([]);
  const [reviewTypeOptions, setReviewTypeOptions] = useState([]);
  const [workTypeOptions, setWorkTypeOptions] = useState([]);
  const [industryTypeOptions, setIndustryTypeOptions] = useState([]);
  const [clientTypeOptions, setClientTypeOptions] = useState([]);
  const [templateApplicability, setTemplateApplicability] = useState({
    reviewTypeId: null,
    workTypeIds: [],
    industryTypeIds: [],
    clientTypeIds: [],
  });
  const autoSaveTimerRef = React.useRef(null);
  const configurationLoadedRef = React.useRef(false);
  const skipNextConfigurationSaveRef = React.useRef(false);
  const [stages, setStages] = useState(defaultStages);
  const [stageDialog, setStageDialog] = useState(null);
  const [stageForm, setStageForm] = useState(blankStage);
  const [stageToDelete, setStageToDelete] = useState(null);
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [workflowConfiguration, setWorkflowConfiguration] = useState(
    defaultWorkflowConfiguration
  );
  const [workflowRoles, setWorkflowRoles] = useState([]);
  const [isPreparationDialogOpen, setIsPreparationDialogOpen] = useState(false);
  const [preparationForm, setPreparationForm] = useState(null);
  const [assignmentDialog, setAssignmentDialog] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState(null);
  const [isWorkflowPreviewOpen, setIsWorkflowPreviewOpen] = useState(false);
  const [isTemplatePreviewOpen, setIsTemplatePreviewOpen] =
    useState(initialPreview);
  const [previewChecklist, setPreviewChecklist] = useState(null);
  const [publishReadinessIssues, setPublishReadinessIssues] = useState(null);
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isDraftVersionConfirmationOpen, setIsDraftVersionConfirmationOpen] =
    useState(false);

  React.useEffect(() => {
    setIsTemplatePreviewOpen(initialPreview);
  }, [template?.id, initialPreview]);

  React.useEffect(() => {
    setIsHistoryDrawerOpen(initialHistory);
  }, [template?.id, initialHistory]);

  React.useEffect(() => {
    setCurrentTemplateVersion(template?.currentVersion || null);
    setIsTemplatePublished(Boolean(template?.currentVersion?.isPublished));
  }, [template?.id]);

  React.useEffect(
    () => () => window.clearTimeout(autoSaveTimerRef.current),
    []
  );

  React.useEffect(() => {
    let isCurrent = true;

    const loadConfiguration = async () => {
      const previewVersionId = initialPreview
        ? template.currentVersionId
        : null;
      const [
        configurationResponse,
        checklistsResponse,
        reportsResponse,
        versionsResponse,
        rolesResponse,
        workTypesResponse,
        industryTypesResponse,
        clientTypesResponse,
        reviewTypesResponse,
      ] = await Promise.all([
        suppliedTemplate?.id ? getReviewTemplateConfiguration(suppliedTemplate.id, previewVersionId) : Promise.resolve({ status: true, data: {} }),
        getChecklists(),
        getReportTemplates(),
        suppliedTemplate?.id ? getReviewTemplateVersionList(suppliedTemplate.id) : Promise.resolve({ status: true, data: [] }),
        getApplicationRoles(),
        getWorkTypes(),
        getIndustryTypes(),
        getClientTypes(),
        getReviewTypes(),
      ]);
      if (!isCurrent) return;

      if (!reviewTypesResponse?.status)
        throw new Error("Unable to load review types.");
      setReviewTypeOptions(reviewTypesResponse.data || []);
      if (rolesResponse?.status) {
        setWorkflowRoles(
          (rolesResponse.data || [])
            .filter(
              (role) => role.status === undefined || Number(role.status) === 1
            )
            .map((role) => ({
              id: Number(role.id),
              name: role.name || role.roleName || "",
            }))
            .filter((role) => role.id && role.name)
        );
      } else {
        setWorkflowRoles([]);
      }
      if (workTypesResponse?.status) {
        setWorkTypeOptions(
          (workTypesResponse.data || []).map((item) => ({
            value: Number(item.id),
            label: item.name,
          }))
        );
      }
      if (industryTypesResponse?.status) {
        setIndustryTypeOptions(
          (industryTypesResponse.data || []).map((item) => ({
            value: Number(item.id),
            label: item.name,
          }))
        );
      }
      if (clientTypesResponse?.status) {
        setClientTypeOptions(
          (clientTypesResponse.data || []).map((item) => ({
            value: Number(item.id),
            label: item.name,
          }))
        );
      }

      if (checklistsResponse?.status) {
        setChecklistOptions(
          (checklistsResponse.data || []).map((item) => ({
            id: item.id,
            name: item.name,
            standard: item.standardType?.name || "Standard not specified",
            workType:
              item.workTypes
                ?.map((workType) => workType.workType?.name)
                .filter(Boolean)
                .join(", ") || "Work type not specified",
            questions: item.questions?.length || 0,
            description: item.description || "",
          }))
        );
      }
      if (reportsResponse?.status) {
        // Report formats are reusable only after their author has published them.
        setReportOptions(
          (reportsResponse.data || [])
            .filter((item) => item.status === "Published")
            .map((item) => ({
              id: item.id,
              name: item.name,
              category: item.reportTypeName || "Report Format",
              formatType: "Report Template",
              updated: item.updatedAt
                ? new Date(item.updatedAt).toLocaleDateString()
                : "Not yet updated",
            }))
        );
      }
      if (versionsResponse?.status) {
        const versions = [...(versionsResponse.data || [])].sort(
          (a, b) => b.versionNumber - a.versionNumber
        );
        setTemplateVersions(versions);
        const selectedVersionId =
          template?.editingVersionId ||
          template?.draftVersionId ||
          template?.currentVersionId;
        const currentVersion =
          versions.find(
            (version) => Number(version.id) === Number(selectedVersionId)
          ) ||
          [...versions].sort((a, b) => b.versionNumber - a.versionNumber)[0];
        setCurrentTemplateVersion(currentVersion || null);
        setIsTemplatePublished(Boolean(currentVersion?.isPublished));
        setTemplateInformation((current) => ({
          ...current,
          version: formatPreviewVersion(currentVersion),
          status: currentVersion?.isPublished ? "Published" : "Draft",
        }));
      }
      if (!configurationResponse?.status) {
        ErrorNotify(
          configurationResponse?.message ||
            "Unable to load the saved template configuration."
        );
        return;
      }

      const configuration = configurationResponse.data || {};
      skipNextConfigurationSaveRef.current = true;
      setStages(
        (configuration.workflowStages || []).length
          ? configuration.workflowStages.map((stage, index) => ({
              name: stage.name,
              description: stage.description || "",
              role: stage.assignedRole,
              stageType: stage.stageType,
              optional: Boolean(stage.isOptional),
              tone: stageTones[index % stageTones.length],
            }))
          : defaultStages
      );
      if (configuration.workflow) {
        setWorkflowConfiguration({
          engagementPreparationResponsibleRoleId:
            configuration.workflow.engagementPreparationResponsibleRoleId ||
            null,
          engagementPreparationResponsibleRoleName:
            configuration.workflow.engagementPreparationResponsibleRoleName ||
            "",
          additionalAuthorizedRoleIds:
            configuration.workflow.additionalAuthorizedRoleIds || [],
          readinessRequirements: {
            ...defaultWorkflowConfiguration.readinessRequirements,
            ...(configuration.workflow.readinessRequirements || {}),
          },
          checklistAssignments: (
            configuration.workflow.checklistAssignments || []
          ).map((assignment) => ({
            checklistId: assignment.checklistId,
            primaryReviewerRole: assignment.primaryReviewerRole || "",
            signOffRole: assignment.signOffRole || "",
            reportTemplateId: assignment.reportTemplateId || null,
            required: assignment.isRequired !== false,
            reviewLevels: assignment.reviewLevels || [],
          })),
          finalSignOffs: (configuration.workflow.finalSignOffs || []).length
            ? (configuration.workflow.finalSignOffs || []).map((signOff) => ({
                role: signOff.role,
                required: signOff.isRequired !== false,
              }))
            : defaultWorkflowConfiguration.finalSignOffs,
        });
      }
      setChecklistSections(
        (configuration.checklistSections || []).map((section, index) => ({
          id: `checklist-section-${index}`,
          name: section.name,
          checklistIds: section.checklistIds || [],
        }))
      );
      setReportSections(
        (configuration.reportSections || []).map((section, index) => ({
          id: `report-section-${index}`,
          name: section.name,
          reportIds: section.reportTemplateIds || [],
        }))
      );
      setReportConfigurations(
        Object.fromEntries(
          (configuration.reportSections || []).flatMap((section) =>
            (section.reports || []).map((report) => [
              report.reportTemplateId,
              {
                scope: report.scope || "engagement",
                generateAt:
                  report.generateAt || report.availabilityTrigger || "manual",
                finalizeAt: report.finalizeAt || "",
                appliesTo: report.appliesTo || "all",
                checklistIds: report.checklistIds || [],
                allowManualRegeneration: Boolean(
                  report.allowManualRegeneration
                ),
                trigger:
                  report.availabilityTrigger || report.generateAt || "manual",
                stage: report.triggerStage || "",
                roles: report.allowedRoles || [],
                approvedFindingsOnly: Boolean(
                  report.includeApprovedFindingsOnly
                ),
                completedChecklistsOnly: Boolean(
                  report.includeCompletedChecklistsOnly
                ),
                includeReferences: Boolean(report.includeReferences),
              },
            ])
          )
        )
      );
      if (configuration.findings) {
        setFindingCategories(
          configuration.findings.categories || defaultFindingCategories
        );
        setSeverityLevels(
          configuration.findings.severityLevels || defaultSeverityLevels
        );
        setRequiredFindingFields(configuration.findings.requiredFields || []);
      }
      setReferences(
        (configuration.references || []).map((reference) => ({
          id: reference.id,
          title: reference.title,
          type: reference.type,
          version: reference.version,
          source: reference.source,
          value: reference.value,
          description: reference.description || "",
        }))
      );
      if (configuration.settings) {
        setTemplateSettings((current) => ({
          ...current,
          ...configuration.settings,
        }));
      }
      setTemplateApplicability({
        reviewTypeId: configuration.applicability?.reviewTypeId ?? null,
        workTypeIds: configuration.applicability?.workTypeIds || [],
        industryTypeIds: configuration.applicability?.industryTypeIds || [],
        clientTypeIds: configuration.applicability?.clientTypeIds || [],
      });
      setTemplateInformation((current) => ({
        ...current,
        code: configuration.applicability?.templateCode || "",
        tags: configuration.applicability?.tags || [],
      }));
      setDocumentFolders(configuration.documentFolders || []);
      configurationLoadedRef.current = true;
    };
    loadConfiguration().catch((error) => {
      console.error(error);
      if (isCurrent) ErrorNotify("Unable to load the template configuration.");
    });
    return () => {
      isCurrent = false;
    };
  }, [suppliedTemplate?.id]);

  const closeTemplatePreview = () => {
    setIsTemplatePreviewOpen(false);
    if (previewOnly) closeAction();
  };

  const closeChecklistPreview = () => setPreviewChecklist(null);

  const editTemplateFromPreview = () => {
    if (onEditTemplate) onEditTemplate(template);
    else closeTemplatePreview();
  };

  const duplicateTemplateFromPreview = async () => {
    if (!template?.id || isDuplicating) return;
    setIsDuplicating(true);
    try {
      const response = await duplicateReviewTemplate(template.id);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to duplicate the review template."
        );
      if (onDuplicateTemplate) onDuplicateTemplate(response.data);
      else closeTemplatePreview();
    } catch (error) {
      console.error(error);
      ErrorNotify(error.message || "Unable to duplicate the review template.");
    } finally {
      setIsDuplicating(false);
    }
  };

  const startTemplateTour = () => {
    setActive("information");
    setExpandedGuidance("about");
    window.setTimeout(
      () =>
        startGuidedTour([
          {
            target: '[data-template-tour="outline"]',
            title: "Template outline",
            description:
              "Use the outline to move through each part of the review template.",
          },
          {
            target: '[data-template-tour="information"]',
            title: "Template details",
            description:
              "Enter the template identity, owner, status, and effective date here.",
          },
          {
            target: '[data-template-tour="template-actions"]',
            title: "Preview and publish",
            description: "Preview the template, then publish when it is ready.",
          },
          {
            target: '[data-template-tour="preview"]',
            title: "Preview",
            description:
              "Preview the template before publishing to confirm the reviewer experience.",
          },
        ]),
      0
    );
  };
  const [checklistSections, setChecklistSections] = useState(
    initialChecklistSections
  );
  const [checklistOptions, setChecklistOptions] = useState([]);
  const [isChecklistLibraryOpen, setIsChecklistLibraryOpen] = useState(false);
  const [checklistSearch, setChecklistSearch] = useState("");
  const [selectedChecklistIds, setSelectedChecklistIds] = useState([]);
  const [selectedChecklistSection, setSelectedChecklistSection] = useState("");
  const [isCreateSectionOpen, setIsCreateSectionOpen] = useState(false);
  const [editingChecklistSection, setEditingChecklistSection] = useState(null);
  const [newSectionName, setNewSectionName] = useState("");
  const [reportSections, setReportSections] = useState(initialReportSections);
  const [reportOptions, setReportOptions] = useState([]);
  const [isReportLibraryOpen, setIsReportLibraryOpen] = useState(false);
  const [reportCategoryFilter, setReportCategoryFilter] = useState("");
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [reportDrawerStep, setReportDrawerStep] = useState(1);
  const [reportAvailability, setReportAvailability] = useState(
    defaultReportAvailability
  );
  const [reportConfigurations, setReportConfigurations] = useState({});
  const [findingCategories, setFindingCategories] = useState(
    defaultFindingCategories
  );
  const [severityLevels, setSeverityLevels] = useState(defaultSeverityLevels);
  const [severityForm, setSeverityForm] = useState(null);
  const [requiredFindingFields, setRequiredFindingFields] = useState([
    "Title",
    "Description",
    "Category",
    "Severity",
    "Recommendation",
  ]);
  const [isFindingCategoryModalOpen, setIsFindingCategoryModalOpen] =
    useState(false);
  const [newFindingCategory, setNewFindingCategory] = useState("");
  const [references, setReferences] = useState(initialReferences);
  const [referenceForm, setReferenceForm] = useState(blankReference);
  const [referenceErrors, setReferenceErrors] = useState({});
  const [isReferenceDrawerOpen, setIsReferenceDrawerOpen] = useState(false);
  const [editingReferenceId, setEditingReferenceId] = useState(null);
  const [selectedReferenceId, setSelectedReferenceId] =
    useState("peer-review-manual");
  const [referencePreview, setReferencePreview] = useState(null);
  const [templateSettings, setTemplateSettings] = useState(
    defaultTemplateSettings
  );
  const [documentFolders, setDocumentFolders] = useState([]);
  const [documentFolderForm, setDocumentFolderForm] = useState(null);
  const outline = buildOutline(
    fixedLifecycle,
    checklistSections,
    reportSections,
    references,
    documentFolders
  );
  const name = templateInformation.name;
  const description = templateInformation.description;

  const applicabilityLabel = (options, selectedIds, allLabel) => {
    if (!selectedIds.length) return allLabel;
    const selectedLabels = options
      .filter((option) => selectedIds.includes(option.value))
      .map((option) => option.label);
    return selectedLabels.length ? selectedLabels.join(", ") : "Not available";
  };

  const openDocumentFolderForm = () =>
    setDocumentFolderForm({ name: "", parentPath: "client-documents" });

  const addTemplateDocumentFolder = (event) => {
    event.preventDefault();
    const name = documentFolderForm?.name?.trim().replace(/\s+/g, " ") || "";
    const parentPath = documentFolderForm?.parentPath || "";
    const segment = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    if (!name || !segment) return;
    const path = `${parentPath}/${segment}`;
    if (documentFolders.some((folder) => folder.path === path)) {
      ErrorNotify(
        "A folder with this name already exists in the selected location."
      );
      return;
    }
    setDocumentFolders((current) => [
      ...current,
      {
        path,
        name,
        isSystem: false,
        sortOrder:
          Math.max(...current.map((folder) => folder.sortOrder || 0)) + 1,
      },
    ]);
    setDocumentFolderForm(null);
  };

  const saveConfiguration = async () => {
    setInformationSubmitted(true);
    if (!templateInformation.name.trim()) throw new Error("Enter a Template Name before saving.");
    if (isTemplatePublished)
      throw new Error(
        "Published template versions are read-only. Create a draft version to make changes."
      );
    if (!templateApplicability.reviewTypeId)
      throw new Error("Select a Review Type before saving.");
    if (!templateApplicability.workTypeIds.length)
      throw new Error("Select at least one Work Type before saving.");
    if (!templateApplicability.industryTypeIds.length)
      throw new Error("Select at least one Industry Type before saving.");
    setAutoSaveStatus("saving");
    let savedTemplate = template || createdTemplateRef.current;
    if (!savedTemplate?.id) {
      const payload = new FormData();
      payload.append("name", templateInformation.name.trim());
      payload.append("description", templateInformation.description.trim());
      payload.append("reviewTypeId", templateApplicability.reviewTypeId);
      const created = await postRawData("/reviewtemplate/createtemplate", "POST", payload);
      if (!created?.status || !created.data?.id) throw new Error(created?.message || "Unable to create the review template.");
      savedTemplate = created.data;
      createdTemplateRef.current = savedTemplate;
      setCreatedTemplate(savedTemplate);
    } else if (savedTemplate.name !== templateInformation.name || savedTemplate.description !== templateInformation.description) {
      const payload = new FormData();
      payload.append("id", savedTemplate.id);
      payload.append("name", templateInformation.name.trim());
      payload.append("description", templateInformation.description.trim());
      payload.append("currentVersionId", savedTemplate.currentVersionId);
      const updated = await postRawData("/reviewtemplate/updatetemplate", "POST", payload);
      if (!updated?.status) throw new Error(updated?.message || "Unable to save template details.");
    }
    const templateCode = templateInformation.code.trim() || `RT-${String(savedTemplate.id).padStart(6, "0")}`;
    const tags = normalizeTags([...templateInformation.tags, tagInput]);
    setTemplateInformation((current) => ({ ...current, code: templateCode, tags }));
    setTagInput("");
    const response = await saveReviewTemplateConfiguration(savedTemplate.id, {
      workflowStages: stages.map((stage, index) => ({
        name: stage.name,
        description: stage.description,
        assignedRole: stage.role,
        stageType: stage.stageType,
        isOptional: Boolean(stage.optional),
        sortOrder: index,
      })),
      workflow: {
        engagementPreparationResponsibleRoleId:
          workflowConfiguration.engagementPreparationResponsibleRoleId || null,
        additionalAuthorizedRoleIds:
          workflowConfiguration.additionalAuthorizedRoleIds || [],
        readinessRequirements: workflowConfiguration.readinessRequirements,
        checklistAssignments: workflowConfiguration.checklistAssignments.map(
          (assignment) => ({
            checklistId: assignment.checklistId,
            primaryReviewerRole: assignment.primaryReviewerRole,
            signOffRole: assignment.signOffRole || null,
            reportTemplateId: assignment.reportTemplateId || null,
            isRequired: assignment.required,
            reviewLevels: assignment.reviewLevels || [],
          })
        ),
        finalSignOffs: workflowConfiguration.finalSignOffs.map(
          (signOff, index) => ({
            role: signOff.role,
            sortOrder: index,
            isRequired: signOff.required,
          })
        ),
      },
      checklistSections: checklistSections.map((section, index) => ({
        name: section.name,
        checklistIds: section.checklistIds,
        sortOrder: index,
      })),
      reportSections: reportSections.map((section, index) => ({
        name: section.name,
        reportTemplateIds: section.reportIds,
        reports: section.reportIds.map((reportTemplateId) => {
          const configuration =
            reportConfigurations[reportTemplateId] || defaultReportAvailability;
          return {
            reportTemplateId,
            scope: configuration.scope,
            generateAt: configuration.generateAt,
            finalizeAt: configuration.finalizeAt || null,
            appliesTo: configuration.appliesTo,
            checklistIds: configuration.checklistIds || [],
            allowManualRegeneration: Boolean(
              configuration.allowManualRegeneration
            ),
            availabilityTrigger: configuration.trigger,
            triggerStage: configuration.stage || null,
            allowedRoles: configuration.roles,
            includeApprovedFindingsOnly: configuration.approvedFindingsOnly,
            includeCompletedChecklistsOnly:
              configuration.completedChecklistsOnly,
            includeReferences: configuration.includeReferences,
          };
        }),
        sortOrder: index,
      })),
      findings: {
        categories: findingCategories,
        severityLevels,
        requiredFields: requiredFindingFields,
      },
      references: references.map((reference, index) => ({
        id: reference.id,
        title: reference.title,
        type: reference.type,
        version: reference.version,
        source: reference.source,
        value: reference.value,
        description: reference.description,
        sortOrder: index,
      })),
      settings: templateSettings,
      documentFolders,
      applicability: { ...templateApplicability, templateCode, tags },
    });
    if (!response?.status)
      throw new Error(
        response?.message || "Unable to save the template configuration."
      );
    setAutoSaveStatus("saved");
    if (!suppliedTemplate?.id) onCreated?.(savedTemplate);
  };

  const scheduleAutoSave = () => {
    if (isTemplatePublished || !template?.id || isManualSavePending) return;
    if (!templateInformation.name.trim() || !templateApplicability.reviewTypeId ||
        !templateApplicability.workTypeIds.length || !templateApplicability.industryTypeIds.length) return;
    setAutoSaveStatus("saving");
    window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = window.setTimeout(() => {
      saveConfiguration().catch((error) => {
        console.error(error);
        setAutoSaveStatus("error");
        ErrorNotify(error.message);
      });
    }, 800);
  };
  React.useEffect(() => {
    if (!configurationLoadedRef.current) return;
    if (skipNextConfigurationSaveRef.current) {
      skipNextConfigurationSaveRef.current = false;
      return;
    }
    scheduleAutoSave();
  }, [
    stages,
    workflowConfiguration,
    checklistSections,
    reportSections,
    reportConfigurations,
    findingCategories,
    severityLevels,
    requiredFindingFields,
    references,
    templateSettings,
    documentFolders,
    templateApplicability,
    templateInformation.name,
    templateInformation.description,
    templateInformation.code,
    templateInformation.tags.join(","),
  ]);

  const updateTemplateInformation = (field, value) => {
    setTemplateInformation((current) => ({ ...current, [field]: value }));
  };

  const saveTemplate = async () => {
    if (isManualSavePending) return false;
    window.clearTimeout(autoSaveTimerRef.current);
    setInformationSubmitted(true);
    if (!templateInformation.name.trim() || !templateApplicability.reviewTypeId ||
        !templateApplicability.workTypeIds.length || !templateApplicability.industryTypeIds.length) {
      setActive("information");
      window.setTimeout(() => {
        const firstInvalid = document.querySelector('.review-template-studio__information [aria-invalid="true"]');
        firstInvalid?.focus();
        firstInvalid?.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 0);
      return false;
    }
    setIsManualSavePending(true);
    try {
      await saveConfiguration();
      SuccessNotify("Template changes saved.");
      return true;
    } catch (error) {
      console.error(error);
      setAutoSaveStatus("error");
      ErrorNotify(
        error.message || "Unable to save the template configuration."
      );
      return false;
    } finally {
      setIsManualSavePending(false);
    }
  };

  const publishTemplate = async () => {
    if (!template?.id || isTemplatePublished) return;
    if (!(await saveTemplate())) return;
    try {
      const response = await publishReviewTemplate(template.id);
      if (!response?.status) {
        if (Array.isArray(response?.data) && response.data.length > 0) {
          setPublishReadinessIssues(response.data);
          return;
        }
        throw new Error(
          response?.message || "Unable to publish the review template."
        );
      }
      setIsTemplatePublished(true);
      setCurrentTemplateVersion((current) =>
        current ? { ...current, isPublished: 1 } : current
      );
      setTemplateInformation((current) => ({
        ...current,
        status: "Published",
      }));
      SuccessNotify("Template published.");
    } catch (error) {
      ErrorNotify(error.message || "Unable to publish the review template.");
    }
  };

  const createDraftVersion = async () => {
    if (!template?.id || isManualSavePending) return;
    setIsManualSavePending(true);
    try {
      const response = await createReviewTemplateDraftVersion(template.id);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to create an editable draft version."
        );
      const versionsResponse = await getReviewTemplateVersionList(template.id);
      const versions = versionsResponse?.status
        ? [...(versionsResponse.data || [])].sort(
            (a, b) => b.versionNumber - a.versionNumber
          )
        : [];
      const draft =
        versions.find((version) => !version.isPublished && !version.isLocked) ||
        versions[0] ||
        null;
      setTemplateVersions(versions);
      setCurrentTemplateVersion(draft);
      setIsTemplatePublished(false);
      setTemplateInformation((current) => ({
        ...current,
        version: formatPreviewVersion(draft),
        status: "Draft",
      }));
      SuccessNotify(response.message || "Draft version created.");
    } catch (error) {
      ErrorNotify(
        error.message || "Unable to create an editable draft version."
      );
    } finally {
      setIsManualSavePending(false);
    }
  };

  const requestDraftVersion = () => {
    if (!template?.id || isManualSavePending) return;
    if (template.draftVersionId) {
      createDraftVersion();
      return;
    }
    setIsDraftVersionConfirmationOpen(true);
  };

  const confirmDraftVersion = () => {
    setIsDraftVersionConfirmationOpen(false);
    createDraftVersion();
  };
  const activeOutlineIndex = outline.findIndex(([id]) => id === active);
  const previousOutline = outline[activeOutlineIndex - 1];
  const nextOutline = outline[activeOutlineIndex + 1];

  const openAddStage = () => {
    setStageForm(blankStage);
    setStageDialog({ mode: "add" });
  };

  const openEditStage = (index) => {
    const stage = stages[index];
    setStageForm({
      name: stage.name,
      description: stage.description || "",
      role: stage.role || "Template Owner",
      stageType: stage.stageType || "manual",
      optional: Boolean(stage.optional),
    });
    setStageDialog({ mode: "edit", index });
  };

  const closeStageDialog = () => setStageDialog(null);

  const updateStageForm = (field, value) =>
    setStageForm((current) => ({ ...current, [field]: value }));

  const saveStage = (event) => {
    event.preventDefault();
    const name = stageForm.name.trim();
    if (!name) return;
    const stage = {
      name,
      description:
        stageForm.description.trim() ||
        "Define what happens during this stage.",
      role: stageForm.stageType === "system" ? "System" : stageForm.role,
      stageType: stageForm.stageType,
      optional: stageForm.optional,
    };
    setStages((current) => {
      if (stageDialog.mode === "edit")
        return current.map((item, index) =>
          index === stageDialog.index ? { ...item, ...stage } : item
        );
      return [
        ...current,
        { ...stage, tone: stageTones[current.length % stageTones.length] },
      ];
    });
    closeStageDialog();
  };

  const deleteStage = () => {
    if (stageToDelete === null) return;
    setStages((current) =>
      current.filter((_, index) => index !== stageToDelete)
    );
    setStageToDelete(null);
  };

  const confirmPendingDeletion = () => {
    pendingDeletion?.onConfirm?.();
    setPendingDeletion(null);
  };

  const toggleReadinessRequirement = (requirement) =>
    setWorkflowConfiguration((current) => ({
      ...current,
      readinessRequirements: {
        ...current.readinessRequirements,
        [requirement]: !current.readinessRequirements[requirement],
      },
    }));

  const preparationOwnerName = (configuration = workflowConfiguration) => {
    const selectedRole = workflowRoles.find(
      (role) =>
        role.id === Number(configuration.engagementPreparationResponsibleRoleId)
    );
    return (
      selectedRole?.name ||
      configuration.engagementPreparationResponsibleRoleName ||
      ""
    );
  };

  const openPreparationConfiguration = () => {
    setPreparationForm({
      responsibleRoleId:
        workflowConfiguration.engagementPreparationResponsibleRoleId || "",
      additionalAuthorizedRoleIds:
        workflowConfiguration.additionalAuthorizedRoleIds || [],
      readinessRequirements: { ...workflowConfiguration.readinessRequirements },
    });
    setIsPreparationDialogOpen(true);
  };

  const togglePreparationAuthorizedRole = (roleId) =>
    setPreparationForm((current) => ({
      ...current,
      additionalAuthorizedRoleIds: current.additionalAuthorizedRoleIds.includes(
        roleId
      )
        ? current.additionalAuthorizedRoleIds.filter((id) => id !== roleId)
        : [...current.additionalAuthorizedRoleIds, roleId],
    }));

  const savePreparationConfiguration = (event) => {
    event.preventDefault();
    const responsibleRoleId = preparationForm?.responsibleRoleId
      ? Number(preparationForm.responsibleRoleId)
      : null;
    const responsibleRole = workflowRoles.find(
      (role) => role.id === responsibleRoleId
    );
    setWorkflowConfiguration((current) => ({
      ...current,
      engagementPreparationResponsibleRoleId: responsibleRoleId,
      engagementPreparationResponsibleRoleName: responsibleRole?.name || "",
      additionalAuthorizedRoleIds: (
        preparationForm?.additionalAuthorizedRoleIds || []
      ).filter((roleId) => roleId !== responsibleRoleId),
      readinessRequirements:
        preparationForm?.readinessRequirements || current.readinessRequirements,
    }));
    setIsPreparationDialogOpen(false);
    setPreparationForm(null);
  };

  const openChecklistAssignment = (checklistId) => {
    const configured = workflowConfiguration.checklistAssignments.find(
      (item) => item.checklistId === checklistId
    );
    setAssignmentForm(
      configured || {
        checklistId,
        primaryReviewerRole: "Reviewer",
        signOffRole: "Review Manager",
        reportTemplateId: null,
        required: true,
        reviewLevels: [],
      }
    );
    setAssignmentDialog(checklistId);
  };

  const updateAssignmentForm = (field, value) =>
    setAssignmentForm((current) => ({ ...current, [field]: value }));

  const addAssignmentReviewLevel = () =>
    setAssignmentForm((current) => ({
      ...current,
      reviewLevels: [
        ...(current.reviewLevels || []),
        {
          name: `Additional review ${current.reviewLevels?.length + 1 || 1}`,
          role: "",
          sortOrder: current.reviewLevels?.length || 0,
        },
      ],
    }));

  const updateAssignmentReviewLevel = (index, field, value) =>
    setAssignmentForm((current) => ({
      ...current,
      reviewLevels: current.reviewLevels.map((level, levelIndex) =>
        levelIndex === index ? { ...level, [field]: value } : level
      ),
    }));

  const removeAssignmentReviewLevel = (index) =>
    setAssignmentForm((current) => ({
      ...current,
      reviewLevels: current.reviewLevels.filter(
        (_, levelIndex) => levelIndex !== index
      ),
    }));

  const saveChecklistAssignment = (event) => {
    event.preventDefault();
    if (!assignmentForm?.primaryReviewerRole) return;
    setWorkflowConfiguration((current) => ({
      ...current,
      checklistAssignments: [
        ...current.checklistAssignments.filter(
          (item) => item.checklistId !== assignmentForm.checklistId
        ),
        assignmentForm,
      ],
    }));
    setAssignmentDialog(null);
    setAssignmentForm(null);
  };

  const updateFinalSignOff = (index, role) =>
    setWorkflowConfiguration((current) => ({
      ...current,
      finalSignOffs: current.finalSignOffs.map((item, itemIndex) =>
        itemIndex === index ? { ...item, role } : item
      ),
    }));

  const addFinalSignOff = () =>
    setWorkflowConfiguration((current) => ({
      ...current,
      finalSignOffs: [...current.finalSignOffs, { role: "", required: true }],
    }));

  const removeFinalSignOff = (index) =>
    setWorkflowConfiguration((current) => ({
      ...current,
      finalSignOffs: current.finalSignOffs.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));

  const moveFinalSignOff = (index, direction) =>
    setWorkflowConfiguration((current) => {
      const destination = index + direction;
      if (destination < 0 || destination >= current.finalSignOffs.length)
        return current;
      const finalSignOffs = [...current.finalSignOffs];
      [finalSignOffs[index], finalSignOffs[destination]] = [
        finalSignOffs[destination],
        finalSignOffs[index],
      ];
      return { ...current, finalSignOffs };
    });
  const filteredChecklists = checklistOptions.filter((checklist) =>
    checklist.name.toLowerCase().includes(checklistSearch.trim().toLowerCase())
  );

  const toggleChecklist = (id) =>
    setSelectedChecklistIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );

  const openChecklistLibrary = (sectionId) => {
    const targetSectionId =
      sectionId ||
      checklistSections.find(
        (section) => section.id === selectedChecklistSection
      )?.id ||
      checklistSections[0]?.id ||
      "";
    setSelectedChecklistSection(targetSectionId);
    setSelectedChecklistIds([]);
    setIsChecklistLibraryOpen(true);
  };

  const linkSelectedChecklists = () => {
    if (!selectedChecklistIds.length) return;
    const targetSectionId =
      selectedChecklistSection || checklistSections[0]?.id;
    if (!targetSectionId) return;
    setChecklistSections((current) =>
      current.map((section) =>
        section.id === targetSectionId
          ? {
              ...section,
              checklistIds: [
                ...section.checklistIds,
                ...selectedChecklistIds.filter(
                  (id) => !section.checklistIds.includes(id)
                ),
              ],
            }
          : section
      )
    );
    setSelectedChecklistIds([]);
    setIsChecklistLibraryOpen(false);
  };

  const closeChecklistSectionDrawer = () => {
    setIsCreateSectionOpen(false);
    setEditingChecklistSection(null);
    setNewSectionName("");
  };

  const openCreateChecklistSection = () => {
    setEditingChecklistSection(null);
    setNewSectionName("");
    setIsCreateSectionOpen(true);
  };

  const openEditChecklistSection = (section) => {
    setEditingChecklistSection(section);
    setNewSectionName(section.name);
    setIsCreateSectionOpen(true);
  };

  const saveChecklistSection = (event) => {
    event.preventDefault();
    const sectionName = newSectionName.trim();
    if (!sectionName) return;
    if (editingChecklistSection) {
      setChecklistSections((current) =>
        current.map((section) =>
          section.id === editingChecklistSection.id
            ? { ...section, name: sectionName }
            : section
        )
      );
      closeChecklistSectionDrawer();
      return;
    }
    const id = `${sectionName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")}-${Date.now()}`;
    setChecklistSections((current) => [
      ...current,
      { id, name: sectionName, checklistIds: [] },
    ]);
    setSelectedChecklistSection(id);
    closeChecklistSectionDrawer();
    setIsChecklistLibraryOpen(true);
  };

  const requestDeleteChecklistSection = (section) => {
    const remainingSections = checklistSections.filter(
      (item) => item.id !== section.id
    );
    const linkedChecklistIds = section.checklistIds;
    setPendingDeletion({
      title: "Delete section",
      itemName: section.name,
      confirmLabel: "Delete section",
      description: linkedChecklistIds.length
        ? `This will remove the section and unlink its ${
            linkedChecklistIds.length
          } checklist${
            linkedChecklistIds.length === 1 ? "" : "s"
          } from this template.`
        : "This action cannot be undone.",
      onConfirm: () => {
        setChecklistSections(remainingSections);
        setSelectedChecklistSection((current) =>
          current === section.id ? remainingSections[0]?.id || "" : current
        );
        setSelectedChecklistIds([]);
        setWorkflowConfiguration((current) => ({
          ...current,
          checklistAssignments: current.checklistAssignments.filter(
            (assignment) => !linkedChecklistIds.includes(assignment.checklistId)
          ),
        }));
        setReportConfigurations((current) =>
          Object.fromEntries(
            Object.entries(current).map(([reportId, configuration]) => [
              reportId,
              {
                ...configuration,
                checklistIds: (configuration.checklistIds || []).filter(
                  (id) => !linkedChecklistIds.includes(id)
                ),
              },
            ])
          )
        );
        if (selectedChecklistSection === section.id)
          setIsChecklistLibraryOpen(false);
      },
    });
  };
  const reportCategories = [
    ...new Set(reportOptions.map((report) => report.category)),
  ]
    .filter(Boolean)
    .sort();
  const filteredReports = reportOptions.filter(
    (report) =>
      !reportCategoryFilter || report.category === reportCategoryFilter
  );

  const toggleReport = (id) =>
    setSelectedReportIds((current) => (current.includes(id) ? [] : [id]));

  const updateReportAvailability = (field, value) =>
    setReportAvailability((current) => ({ ...current, [field]: value }));

  const reportDefaults = (reportId) => {
    const category =
      reportOptions.find((report) => report.id === reportId)?.category || "";
    if (/checklist/i.test(category)) {
      return {
        ...defaultReportAvailability,
        scope: "checklist",
        generateAt: "checklist-completion",
        finalizeAt: "checklist-sign-off",
        trigger: "checklist-completion",
      };
    }
    if (/findings|management|final/i.test(category)) {
      return {
        ...defaultReportAvailability,
        scope: "engagement",
        generateAt: "review-completion",
        finalizeAt: "final-sign-off",
        trigger: "review-completion",
      };
    }
    return { ...defaultReportAvailability };
  };

  const toggleReportRole = (role) =>
    setReportAvailability((current) => ({
      ...current,
      roles: current.roles.includes(role)
        ? current.roles.filter((item) => item !== role)
        : [...current.roles, role],
    }));

  const openReportConfiguration = (reportId) => {
    setSelectedReportIds([reportId]);
    setReportAvailability(
      reportConfigurations[reportId] || reportDefaults(reportId)
    );
    setReportDrawerStep(2);
    setIsReportLibraryOpen(true);
  };

  const continueToReportConfiguration = () => {
    const reportId = selectedReportIds[0];
    if (!reportId) return;
    setReportAvailability(
      reportConfigurations[reportId] || reportDefaults(reportId)
    );
    setReportDrawerStep(2);
  };

  const linkSelectedReports = () => {
    if (!selectedReportIds.length) return;
    const defaultSection = reportSections[0] || {
      id: "report-formats",
      name: "Report Formats",
      reportIds: [],
    };
    setReportSections((current) =>
      current.length
        ? current.map((section) =>
            section.id === defaultSection.id
              ? {
                  ...section,
                  reportIds: [
                    ...section.reportIds,
                    ...selectedReportIds.filter(
                      (id) => !section.reportIds.includes(id)
                    ),
                  ],
                }
              : section
          )
        : [{ ...defaultSection, reportIds: selectedReportIds }]
    );
    setReportConfigurations((current) => ({
      ...current,
      ...Object.fromEntries(
        selectedReportIds.map((id) => [
          id,
          { ...reportDefaults(id), ...reportAvailability },
        ])
      ),
    }));
    setSelectedReportIds([]);
    setReportDrawerStep(1);
    setIsReportLibraryOpen(false);
  };

  const openReportLibrary = () => {
    setSelectedReportIds([]);
    setReportAvailability(defaultReportAvailability);
    setReportDrawerStep(1);
    setIsReportLibraryOpen(true);
  };

  const toggleRequiredFindingField = (field) =>
    setRequiredFindingFields((current) =>
      current.includes(field)
        ? current.filter((item) => item !== field)
        : [...current, field]
    );

  const addFindingCategory = (event) => {
    event.preventDefault();
    const category = newFindingCategory.trim();
    if (
      !category ||
      findingCategories.some(
        (item) => item.toLowerCase() === category.toLowerCase()
      )
    )
      return;
    setFindingCategories((current) => [...current, category]);
    setNewFindingCategory("");
    setIsFindingCategoryModalOpen(false);
  };

  const openSeverityDialog = (severity) =>
    setSeverityForm(
      severity
        ? { ...severity, originalName: severity.name }
        : blankSeverityLevel
    );

  const saveSeverityLevel = (event) => {
    event.preventDefault();
    const name = severityForm.name.trim();
    const detail = severityForm.detail.trim();
    if (
      !name ||
      !detail ||
      severityLevels.some(
        (severity) =>
          severity.name.toLowerCase() === name.toLowerCase() &&
          severity.name !== severityForm.originalName
      )
    )
      return;
    const nextSeverity = { name, detail, tone: severityForm.tone };
    setSeverityLevels((current) =>
      severityForm.originalName
        ? current.map((severity) =>
            severity.name === severityForm.originalName
              ? nextSeverity
              : severity
          )
        : [...current, nextSeverity]
    );
    setSeverityForm(null);
  };

  const updateReferenceForm = (field, value) => {
    setReferenceForm((current) => ({ ...current, [field]: value }));
    setReferenceErrors((current) => ({ ...current, [field]: "" }));
  };

  const setReferenceSource = (source) => {
    setReferenceForm((current) => ({ ...current, source, value: "" }));
    setReferenceErrors((current) => ({ ...current, source: "", value: "" }));
  };

  const selectReferenceDocument = (event) => {
    const file = event.target.files?.[0];
    updateReferenceForm("value", file?.name ?? "");
  };

  const closeReferenceDrawer = () => {
    setIsReferenceDrawerOpen(false);
    setEditingReferenceId(null);
    setReferenceForm(blankReference);
    setReferenceErrors({});
  };

  const openReferenceDrawer = () => {
    setReferenceForm(blankReference);
    setReferenceErrors({});
    setEditingReferenceId(null);
    setSelectedReferenceId("");
    setIsReferenceDrawerOpen(true);
  };

  const addReference = (event) => {
    event.preventDefault();
    const errors = {
      title: referenceForm.title.trim() ? "" : "Enter a title.",
      type: referenceForm.type ? "" : "Select a type.",
      version: referenceForm.version.trim()
        ? ""
        : "Enter a reference or version.",
      value: referenceForm.value.trim()
        ? ""
        : referenceForm.source === "document"
        ? "Select a document."
        : "Enter a URL.",
    };
    setReferenceErrors(errors);
    if (Object.values(errors).some(Boolean)) return;
    const id =
      editingReferenceId ??
      `${referenceForm.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}-${Date.now()}`;
    const reference = {
      ...referenceForm,
      id,
      title: referenceForm.title.trim(),
      version: referenceForm.version.trim(),
      value: referenceForm.value.trim(),
      description: referenceForm.description.trim(),
    };
    setReferences((current) =>
      editingReferenceId
        ? current.map((item) => (item.id === id ? reference : item))
        : [...current, reference]
    );
    setSelectedReferenceId(id);
    closeReferenceDrawer();
  };

  const editReference = (reference) => {
    setReferenceForm({
      title: reference.title,
      type: reference.type,
      version: reference.version,
      source: reference.source,
      value: reference.value,
      description: reference.description,
    });
    setReferenceErrors({});
    setSelectedReferenceId(reference.id);
    setEditingReferenceId(reference.id);
    setIsReferenceDrawerOpen(true);
  };

  const toggleTemplateSetting = (setting) => {
    setTemplateSettings((current) => ({
      ...current,
      [setting]: !current[setting],
    }));
  };

  const settingToggle = (icon, title, description, setting) => (
    <article className="review-template-settings__toggle" key={setting}>
      <span>
        <i className={`bx ${icon}`} />
      </span>
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={templateSettings[setting]}
        className={templateSettings[setting] ? "is-on" : ""}
        onClick={() => toggleTemplateSetting(setting)}
      >
        <i />
      </button>
    </article>
  );
  const linkedChecklistIds = checklistSections.flatMap(
    (section) => section.checklistIds
  );
  const linkedReports = reportSections
    .flatMap((section) =>
      section.reportIds.map((id) => ({
        section: section.name,
        report: reportOptions.find((item) => item.id === id),
      }))
    )
    .filter((item) => item.report);
  const checklistWorkflowAssignments = linkedChecklistIds
    .map((checklistId) => ({
      checklistId,
      checklist: checklistOptions.find((item) => item.id === checklistId),
      assignment: workflowConfiguration.checklistAssignments.find(
        (item) => item.checklistId === checklistId
      ),
    }))
    .filter((item) => item.checklist);
  const completionReports = linkedReports.filter(
    ({ report }) =>
      (reportConfigurations[report.id] || reportDefaults(report.id))
        .generateAt === "review-completion"
  );
  const engagementPreparationOwner = preparationOwnerName();
  const readinessConfiguredCount = readinessRequirementOptions.filter(
    ([key]) => workflowConfiguration.readinessRequirements[key]
  ).length;
  const additionalAuthorizedRoles = (
    workflowConfiguration.additionalAuthorizedRoleIds || []
  )
    .map(
      (roleId) => workflowRoles.find((role) => role.id === Number(roleId))?.name
    )
    .filter(Boolean);
  const additionalReviewLevels = checklistWorkflowAssignments.flatMap(
    ({ checklist, assignment }) =>
      (assignment?.reviewLevels || [])
        .filter((level) => level.name || level.role)
        .map((level) => ({ checklist, ...level }))
  );
  const hasAdditionalReview = additionalReviewLevels.length > 0;
  const configuredWorkflowStages = fixedLifecycle.map(
    ([name, kind, description]) => ({
      name,
      description,
      role:
        kind === "configurable"
          ? "Template configuration"
          : kind === "final"
          ? "Final system state"
          : "System controlled",
    })
  );
  const workflowPreviewDetails = [
    {
      title: "Engagement Preparation",
      summary: `Owner: ${engagementPreparationOwner || "Not configured"}`,
      details: [
        `${readinessConfiguredCount} of ${readinessRequirementOptions.length} readiness requirements configured`,
        ...(additionalAuthorizedRoles.length
          ? [`Also authorized: ${additionalAuthorizedRoles.join(", ")}`]
          : []),
      ],
    },
    {
      title: "Ready for Review",
      summary: "System-controlled readiness gate",
      details: [
        `Checks the configured readiness requirements before review can begin.`,
      ],
    },
    {
      title: "Checklist Review",
      summary: `${checklistWorkflowAssignments.length} checklist review${
        checklistWorkflowAssignments.length === 1 ? "" : "s"
      } configured`,
      details: checklistWorkflowAssignments.length
        ? checklistWorkflowAssignments.map(
            ({ checklist, assignment }) =>
              `${checklist.name}: ${
                assignment?.primaryReviewerRole || "Reviewer not configured"
              }${assignment?.required === false ? " (optional)" : ""}`
          )
        : ["No checklist review assignments configured."],
    },
    {
      title: "Checklist Sign-off",
      summary: `${
        checklistWorkflowAssignments.filter(
          ({ assignment }) => assignment?.signOffRole
        ).length
      } sign-off role${
        checklistWorkflowAssignments.filter(
          ({ assignment }) => assignment?.signOffRole
        ).length === 1
          ? ""
          : "s"
      } configured`,
      details: checklistWorkflowAssignments.length
        ? checklistWorkflowAssignments.map(
            ({ checklist, assignment }) =>
              `${checklist.name}: ${
                assignment?.signOffRole || "No sign-off required"
              }`
          )
        : ["No checklist sign-off assignments configured."],
    },
    {
      title: "Review Completion",
      summary: "System-controlled completion gate",
      details: [
        "Requires checklist completion and sign-off.",
        "Requires findings and conclusions to be resolved.",
        completionReports.length
          ? `Reports generated: ${completionReports
              .map(({ report }) => report.name)
              .join(", ")}`
          : "No reports generated at review completion.",
      ],
    },
    {
      title: "Final Engagement Sign-off",
      summary: `${
        workflowConfiguration.finalSignOffs.length
      } required approval${
        workflowConfiguration.finalSignOffs.length === 1 ? "" : "s"
      }`,
      details: workflowConfiguration.finalSignOffs.length
        ? workflowConfiguration.finalSignOffs.map(
            (signOff, index) =>
              `${index + 1}. ${signOff.role || "Role not configured"}`
          )
        : ["No final approvals configured."],
    },
    {
      title: "Completed",
      summary: "System-controlled final state",
      details: [
        "The engagement is complete after all workflow gates have passed.",
      ],
    },
  ];
  const previewStatus = isTemplatePublished ? "Published" : "Draft";
  const previewVersion = formatPreviewVersion(currentTemplateVersion);
  const previewUpdatedOn = formatPreviewDate(
    currentTemplateVersion?.updatedOn ||
      template?.updatedOn ||
      template?.createdOn
  );
  const selectedReport = reportOptions.find(
    (report) => report.id === selectedReportIds[0]
  );

  const reportTriggerText = (configuration) => {
    const trigger = configuration.generateAt || configuration.trigger;
    return (
      {
        "checklist-completion": "Checklist Completion",
        "checklist-sign-off": "Checklist Sign-off",
        "review-completion": "Review Completion",
        "final-sign-off": "Final Sign-off",
        manual: "Manual",
      }[trigger] || "Manual"
    );
  };
  const checklistsView = (
    <section className="review-template-checklists">
      <section className="review-template-checklists__editor">
        <div className="review-template-checklists__heading">
          <span>
            <i className="bx bx-clipboard" />
          </span>
          <div>
            <h2>Checklists</h2>
            <p>
              Select and arrange the checklists used in this review template.
            </p>
          </div>
          <div className="review-template-checklists__actions">
            <Button
              type="button"
              className="review-template-checklists__create"
              onClick={openCreateChecklistSection}
            >
              <i className="bx bx-folder-plus" /> Create Section
            </Button>
            <Button
              type="button"
              className="review-template-checklists__add"
              onClick={() =>
                checklistSections.length
                  ? openChecklistLibrary()
                  : openCreateChecklistSection()
              }
            >
              <i className="bx bx-plus" /> Add Checklist
            </Button>
          </div>
        </div>
        <aside className="review-template-checklists__library-note">
          <i className="bx bx-info-circle" />
          <span>
            <b>
              You are linking existing checklists from the Checklist Library.
            </b>{" "}
            Manage checklists from the{" "}
            <button type="button">
              Checklist Library <i className="bx bx-link-external" />
            </button>
          </span>
        </aside>
        <div className="review-template-checklists__sections">
          {checklistSections.length ? (
            checklistSections.map((section) => (
              <article
                className="review-template-checklists__section"
                key={section.id}
              >
                <header>
                  <i className="bx bx-grid-vertical" />
                  <h3>{section.name}</h3>
                  <b>{section.checklistIds.length}</b>
                  <ActionMenu
                    label={`${section.name} section`}
                    actions={[
                      {
                        label: "Edit section",
                        icon: <i className="bx bx-pencil" />,
                        onSelect: () => openEditChecklistSection(section),
                      },
                      {
                        label: "Delete section",
                        icon: <i className="bx bx-trash" />,
                        className: "is-danger",
                        onSelect: () => requestDeleteChecklistSection(section),
                      },
                    ]}
                  />
                </header>
                <div>
                  {section.checklistIds.length ? (
                    section.checklistIds.map((checklistId) => {
                      const checklist = checklistOptions.find(
                        (item) => item.id === checklistId
                      );
                      return (
                        checklist && (
                          <article
                            className="review-template-checklists__item"
                            key={checklist.id}
                          >
                            <i className="bx bx-grid-vertical" />
                            <span className="review-template-checklists__item-icon">
                              <i className="bx bx-clipboard" />
                            </span>
                            <div>
                              <h4>{checklist.name}</h4>
                              <p>
                                {checklist.standard} <b>•</b>{" "}
                                {checklist.workType} <b>•</b>{" "}
                                {checklist.questions} Questions
                              </p>
                            </div>
                            <small>Active</small>
                            <button
                              type="button"
                              className="review-template-checklists__remove"
                              aria-label={`Remove ${checklist.name}`}
                              title="Remove checklist"
                              onClick={() =>
                                setPendingDeletion({
                                  title: "Remove Checklist",
                                  itemName: checklist.name,
                                  description:
                                    "This removes the checklist from this review template. The source checklist remains available in the Checklist Library.",
                                  confirmLabel: "Remove",
                                  onConfirm: () => {
                                    setChecklistSections((current) =>
                                      current.map((currentSection) =>
                                        currentSection.id === section.id
                                          ? {
                                              ...currentSection,
                                              checklistIds:
                                                currentSection.checklistIds.filter(
                                                  (id) => id !== checklist.id
                                                ),
                                            }
                                          : currentSection
                                      )
                                    );
                                    setWorkflowConfiguration((current) => ({
                                      ...current,
                                      checklistAssignments:
                                        current.checklistAssignments.filter(
                                          (assignment) =>
                                            assignment.checklistId !==
                                            checklist.id
                                        ),
                                    }));
                                    setReportConfigurations((current) =>
                                      Object.fromEntries(
                                        Object.entries(current).map(
                                          ([reportId, configuration]) => [
                                            reportId,
                                            {
                                              ...configuration,
                                              checklistIds: (
                                                configuration.checklistIds || []
                                              ).filter(
                                                (id) => id !== checklist.id
                                              ),
                                            },
                                          ]
                                        )
                                      )
                                    );
                                  },
                                })
                              }
                            >
                              <i className="bx bx-trash" />
                            </button>
                          </article>
                        )
                      );
                    })
                  ) : (
                    <button
                      type="button"
                      className="review-template-checklists__empty-section"
                      onClick={() => openChecklistLibrary(section.id)}
                    >
                      <i className="bx bx-plus" /> Link an existing checklist
                    </button>
                  )}
                </div>
              </article>
            ))
          ) : (
            <aside className="review-template-studio__empty">
              <i className="bx bx-clipboard" />
              <h2>No checklists added</h2>
              <p>
                Create a section, then choose the checklists for this template.
              </p>
              <Button type="button" onClick={openCreateChecklistSection}>
                <i className="bx bx-folder-plus" /> Create Section
              </Button>
            </aside>
          )}
        </div>
      </section>
      {isChecklistLibraryOpen && (
        <Offcanvas
          isOpen={isChecklistLibraryOpen}
          toggle={() => setIsChecklistLibraryOpen(false)}
          direction="end"
          className="review-template-checklists__drawer"
          aria-label="Add checklist"
        >
          <div className="offcanvas-body">
            <header>
              <div>
                <h2>Add Checklist</h2>
                <p>Select existing checklists to add to this template.</p>
              </div>
              <button
                type="button"
                aria-label="Close checklist library"
                onClick={() => setIsChecklistLibraryOpen(false)}
              >
                <i className="bx bx-x" />
              </button>
            </header>
            <div className="review-template-checklists__drawer-content">
              <label className="review-template-checklists__search">
                Search
                <span className="review-template-checklists__search-field">
                  <i className="bx bx-search" aria-hidden="true" />
                  <Input
                    value={checklistSearch}
                    onChange={(event) => setChecklistSearch(event.target.value)}
                    placeholder="Search checklists..."
                  />
                </span>
              </label>
              <div className="review-template-checklists__filters">
                <label>
                  Standard
                  <Input type="select">
                    <option>All Standards</option>
                    <option>AICPA</option>
                  </Input>
                </label>
                <label>
                  Work Type
                  <Input type="select">
                    <option>All Work Types</option>
                    <option>Review, Audit</option>
                    <option>Review, Compilation</option>
                  </Input>
                </label>
              </div>
              <div className="review-template-checklists__library-list">
                {filteredChecklists.map((checklist) => (
                  <label
                    className={
                      selectedChecklistIds.includes(checklist.id)
                        ? "is-selected"
                        : ""
                    }
                    key={checklist.id}
                  >
                    <Input
                      type="checkbox"
                      checked={selectedChecklistIds.includes(checklist.id)}
                      onChange={() => toggleChecklist(checklist.id)}
                    />
                    <span>
                      <b>{checklist.name}</b>
                      <small>{checklist.standard}</small>
                      <em>
                        {checklist.workType} <i>•</i> {checklist.questions}{" "}
                        Questions
                      </em>
                    </span>
                    <strong>Active</strong>
                  </label>
                ))}
              </div>
              <label className="review-template-checklists__add-under">
                Add under
                <Input
                  type="select"
                  value={selectedChecklistSection}
                  onChange={(event) =>
                    setSelectedChecklistSection(event.target.value)
                  }
                >
                  {checklistSections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </Input>
              </label>
            </div>
            <footer>
              <Button
                type="button"
                className="review-template-checklists__cancel"
                onClick={() => setIsChecklistLibraryOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="review-template-checklists__confirm"
                disabled={!selectedChecklistIds.length}
                onClick={linkSelectedChecklists}
              >
                Add Selected ({selectedChecklistIds.length})
              </Button>
            </footer>
          </div>
        </Offcanvas>
      )}
    </section>
  );
  const reportsView = (
    <section className="review-template-reports">
      <section className="review-template-reports__editor">
        <div className="review-template-reports__heading">
          <span>
            <i className="bx bx-file" />
          </span>
          <div>
            <h2>Report Formats</h2>
            <p>
              Select and arrange the report formats included in this review
              template.
            </p>
          </div>
          <Button
            type="button"
            className="review-template-reports__add"
            onClick={openReportLibrary}
          >
            <i className="bx bx-plus" /> Add Report Format
          </Button>
        </div>
        <aside className="review-template-reports__library-note">
          <i className="bx bx-info-circle" />
          <span>
            <b>
              You are linking existing report formats from the Report Library.
            </b>{" "}
            Manage report formats from the{" "}
            <button type="button">
              Report Library <i className="bx bx-link-external" />
            </button>
          </span>
        </aside>
        <div className="review-template-reports__sections">
          {reportSections.length ? (
            reportSections.map((section) => (
              <article
                className="review-template-reports__section"
                key={section.id}
              >
                <div className="review-template-reports__columns">
                  <span>Report Format</span>
                  <span>Report Type</span>
                  <span>Scope</span>
                  <span>Generate At</span>
                  <span>Finalize At</span>
                  <span>Status</span>
                  <span>Actions</span>
                </div>
                <div>
                  {section.reportIds.map((reportId) => {
                    const report = reportOptions.find(
                      (item) => item.id === reportId
                    );
                    return (
                      report && (
                        <article
                          className="review-template-reports__item"
                          key={report.id}
                        >
                          <i className="bx bx-grid-vertical" />
                          <span className="review-template-reports__item-icon">
                            <i className="bx bx-file" />
                          </span>
                          <div>
                            <h4>{report.name}</h4>
                            <p>
                              AICPA <b>•</b> {report.updated} <b>•</b>{" "}
                              {report.formatType}
                            </p>
                          </div>
                          <em className="review-template-reports__type">
                            {report.category}
                          </em>
                          <span className="review-template-reports__trigger">
                            {(
                              reportConfigurations[report.id] ||
                              reportDefaults(report.id)
                            ).scope === "checklist"
                              ? "Checklist"
                              : "Engagement"}
                          </span>
                          <span className="review-template-reports__trigger">
                            {reportTriggerText(
                              reportConfigurations[report.id] ||
                                reportDefaults(report.id)
                            )}
                          </span>
                          <span className="review-template-reports__trigger">
                            {(
                              reportConfigurations[report.id] ||
                              reportDefaults(report.id)
                            ).finalizeAt
                              ? reportTriggerText({
                                  generateAt: (
                                    reportConfigurations[report.id] ||
                                    reportDefaults(report.id)
                                  ).finalizeAt,
                                })
                              : "—"}
                          </span>
                          <small>Active</small>
                          <div className="review-template-reports__row-actions">
                            <button
                              type="button"
                              aria-label={`Configure ${report.name}`}
                              title="Configure report"
                              onClick={() => openReportConfiguration(report.id)}
                            >
                              <i className="bx bx-pencil" />
                            </button>
                            <button
                              type="button"
                              className="is-danger"
                              aria-label={`Remove ${report.name}`}
                              title="Remove report"
                              onClick={() =>
                                setPendingDeletion({
                                  title: "Remove Report Format",
                                  itemName: report.name,
                                  description:
                                    "This removes the report format from this review template. The source report format remains available in the Report Library.",
                                  confirmLabel: "Remove",
                                  onConfirm: () => {
                                    setReportSections((current) =>
                                      current.map((currentSection) =>
                                        currentSection.id === section.id
                                          ? {
                                              ...currentSection,
                                              reportIds:
                                                currentSection.reportIds.filter(
                                                  (id) => id !== report.id
                                                ),
                                            }
                                          : currentSection
                                      )
                                    );
                                    setReportConfigurations((current) => {
                                      const remaining = { ...current };
                                      delete remaining[report.id];
                                      return remaining;
                                    });
                                    setWorkflowConfiguration((current) => ({
                                      ...current,
                                      checklistAssignments:
                                        current.checklistAssignments.map(
                                          (assignment) =>
                                            assignment.reportTemplateId ===
                                            report.id
                                              ? {
                                                  ...assignment,
                                                  reportTemplateId: null,
                                                }
                                              : assignment
                                        ),
                                    }));
                                  },
                                })
                              }
                            >
                              <i className="bx bx-trash" />
                            </button>
                          </div>
                        </article>
                      )
                    );
                  })}
                </div>
              </article>
            ))
          ) : (
            <aside className="review-template-studio__empty">
              <i className="bx bx-file" />
              <h2>No report formats added</h2>
              <p>Add a report format when this template needs one.</p>
              <Button type="button" onClick={openReportLibrary}>
                <i className="bx bx-plus" /> Add Report Format
              </Button>
            </aside>
          )}
        </div>
      </section>
      {isReportLibraryOpen && (
        <Offcanvas
          isOpen={isReportLibraryOpen}
          toggle={() => setIsReportLibraryOpen(false)}
          direction="end"
          className="review-template-reports__drawer"
          aria-label="Add report format"
        >
          <div className="offcanvas-body">
            <header>
              <div>
                <h2>
                  {reportDrawerStep === 1
                    ? "Add Report Format"
                    : "Configure Report Format"}
                </h2>
                <p>
                  {reportDrawerStep === 1
                    ? "Add a report from the library and configure when it is available."
                    : "Define when and who can generate this report in this template."}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close report library"
                onClick={() => setIsReportLibraryOpen(false)}
              >
                <i className="bx bx-x" />
              </button>
            </header>
            <div className="review-template-reports__steps">
              <span
                className={reportDrawerStep === 1 ? "is-active" : "is-complete"}
              >
                <b>1</b> Select Report
              </span>
              <i />
              <span className={reportDrawerStep === 2 ? "is-active" : ""}>
                <b>2</b> Configure Availability
              </span>
            </div>
            {reportDrawerStep === 1 ? (
              <>
                <div className="review-template-reports__filters">
                  <label>
                    Category
                    <Input
                      type="select"
                      value={reportCategoryFilter}
                      onChange={(event) =>
                        setReportCategoryFilter(event.target.value)
                      }
                    >
                      <option value="">All Categories</option>
                      {reportCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </Input>
                  </label>
                </div>
                <div className="review-template-reports__library-list">
                  {filteredReports.map((report) => (
                    <label
                      className={
                        selectedReportIds.includes(report.id)
                          ? "is-selected"
                          : ""
                      }
                      key={report.id}
                    >
                      <Input
                        type="checkbox"
                        checked={selectedReportIds.includes(report.id)}
                        onChange={() => toggleReport(report.id)}
                      />
                      <span>
                        <b>{report.name}</b>
                        <small>AICPA</small>
                        <em>
                          {report.formatType} <i>•</i> {report.updated}
                        </em>
                      </span>
                      <strong>{report.category}</strong>
                      <u>Active</u>
                    </label>
                  ))}
                </div>
                <footer>
                  <Button
                    type="button"
                    className="review-template-reports__cancel"
                    onClick={() => setIsReportLibraryOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    className="review-template-reports__confirm"
                    disabled={!selectedReportIds.length}
                    onClick={continueToReportConfiguration}
                  >
                    Next <i className="bx bx-right-arrow-alt" />
                  </Button>
                </footer>
              </>
            ) : (
              <section className="review-template-reports__configuration">
                {selectedReport && (
                  <article className="review-template-reports__selected-report">
                    <span>
                      <i className="bx bx-file" />
                    </span>
                    <div>
                      <b>{selectedReport.name}</b>
                      <small>{selectedReport.category}</small>
                    </div>
                  </article>
                )}
                <h3>Workflow</h3>
                <label>
                  Scope <b>*</b>
                  <Input
                    type="select"
                    value={reportAvailability.scope}
                    onChange={(event) =>
                      updateReportAvailability("scope", event.target.value)
                    }
                  >
                    <option value="engagement">Engagement</option>
                    <option value="checklist">Checklist</option>
                  </Input>
                </label>
                <label>
                  Generate At <b>*</b>
                  <Input
                    type="select"
                    value={reportAvailability.generateAt}
                    onChange={(event) =>
                      setReportAvailability((current) => ({
                        ...current,
                        generateAt: event.target.value,
                        trigger: event.target.value,
                      }))
                    }
                  >
                    <option value="manual">Manual</option>
                    <option
                      value="checklist-completion"
                      disabled={reportAvailability.scope !== "checklist"}
                    >
                      Checklist Completion
                    </option>
                    <option
                      value="checklist-sign-off"
                      disabled={reportAvailability.scope !== "checklist"}
                    >
                      Checklist Sign-off
                    </option>
                    <option
                      value="review-completion"
                      disabled={reportAvailability.scope !== "engagement"}
                    >
                      Review Completion
                    </option>
                    <option
                      value="final-sign-off"
                      disabled={reportAvailability.scope !== "engagement"}
                    >
                      Final Sign-off
                    </option>
                  </Input>
                </label>
                <label>
                  Finalize At <b>*</b>
                  <Input
                    type="select"
                    value={reportAvailability.finalizeAt}
                    onChange={(event) =>
                      updateReportAvailability("finalizeAt", event.target.value)
                    }
                  >
                    <option value="">Select finalization point</option>
                    {reportAvailability.scope === "checklist" ? (
                      <>
                        <option value="checklist-completion">
                          Checklist Completion
                        </option>
                        <option value="checklist-sign-off">
                          Checklist Sign-off
                        </option>
                      </>
                    ) : (
                      <>
                        <option value="review-completion">
                          Review Completion
                        </option>
                        <option value="final-sign-off">Final Sign-off</option>
                      </>
                    )}
                  </Input>
                </label>
                {reportAvailability.scope === "checklist" && (
                  <>
                    <h3>Checklist Association</h3>
                    <label>
                      Applies To
                      <Input
                        type="select"
                        value={reportAvailability.appliesTo}
                        onChange={(event) =>
                          updateReportAvailability(
                            "appliesTo",
                            event.target.value
                          )
                        }
                      >
                        <option value="all">All Checklists</option>
                        <option value="selected">Selected Checklists</option>
                      </Input>
                    </label>
                    {reportAvailability.appliesTo === "selected" && (
                      <div className="review-template-reports__rules">
                        {linkedChecklistIds.map((checklistId) => {
                          const checklist = checklistOptions.find(
                            (item) => item.id === checklistId
                          );
                          return (
                            checklist && (
                              <label key={checklistId}>
                                <Input
                                  type="checkbox"
                                  checked={reportAvailability.checklistIds.includes(
                                    checklistId
                                  )}
                                  onChange={() =>
                                    updateReportAvailability(
                                      "checklistIds",
                                      reportAvailability.checklistIds.includes(
                                        checklistId
                                      )
                                        ? reportAvailability.checklistIds.filter(
                                            (id) => id !== checklistId
                                          )
                                        : [
                                            ...reportAvailability.checklistIds,
                                            checklistId,
                                          ]
                                    )
                                  }
                                />
                                {checklist.name}
                              </label>
                            )
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
                <h3>Additional Options</h3>
                <div className="review-template-reports__rules">
                  <label>
                    <Input
                      type="checkbox"
                      checked={reportAvailability.allowManualRegeneration}
                      onChange={(event) =>
                        updateReportAvailability(
                          "allowManualRegeneration",
                          event.target.checked
                        )
                      }
                    />
                    Allow manual regeneration
                  </label>
                </div>
                <footer>
                  <Button
                    type="button"
                    className="review-template-reports__cancel"
                    onClick={() => setReportDrawerStep(1)}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    className="review-template-reports__confirm"
                    disabled={
                      !reportAvailability.finalizeAt ||
                      (reportAvailability.scope === "checklist" &&
                        reportAvailability.appliesTo === "selected" &&
                        !reportAvailability.checklistIds.length)
                    }
                    onClick={linkSelectedReports}
                  >
                    Save Configuration
                  </Button>
                </footer>
              </section>
            )}
          </div>
        </Offcanvas>
      )}
    </section>
  );
  const findingsView = (
    <section className="review-template-findings">
      <section className="review-template-findings__editor">
        <div className="review-template-findings__heading">
          <i className="bx bx-flag" />
          <div>
            <h2>Findings</h2>
            <p>
              Configure how findings are classified and captured during a
              review.
            </p>
          </div>
        </div>
        <section className="review-template-findings__card">
          <header>
            <div>
              <h3>Finding Categories</h3>
              <p>
                Define the categories reviewers can use to classify findings.
              </p>
            </div>
            <Button
              type="button"
              onClick={() => setIsFindingCategoryModalOpen(true)}
            >
              <i className="bx bx-plus" /> Add Category
            </Button>
          </header>
          <div className="review-template-findings__categories">
            {findingCategories.map((category) => (
              <article key={category}>
                <i className="bx bx-grid-vertical" />
                <span>{category}</span>
                <button type="button" aria-label={`Edit ${category}`}>
                  <i className="bx bx-edit-alt" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${category}`}
                  disabled={findingCategories.length === 1}
                  onClick={() =>
                    setPendingDeletion({
                      title: "Delete Finding Category",
                      itemName: category,
                      description:
                        "This category will no longer be available for findings in this review template.",
                      confirmLabel: "Delete",
                      onConfirm: () =>
                        setFindingCategories((current) =>
                          current.filter((item) => item !== category)
                        ),
                    })
                  }
                >
                  <i className="bx bx-trash" />
                </button>
              </article>
            ))}
          </div>
        </section>
        <section className="review-template-findings__card">
          <header>
            <div>
              <h3>Severity Levels</h3>
              <p>
                Define the severity levels reviewers can assign to findings.
              </p>
            </div>
            <Button type="button" onClick={() => openSeverityDialog()}>
              <i className="bx bx-plus" /> Add Severity
            </Button>
          </header>
          <div className="review-template-findings__severities">
            {severityLevels.map((severity) => (
              <article key={severity.name}>
                <span
                  className={`review-template-findings__severity-dot review-template-findings__severity-dot--${severity.tone}`}
                />
                <div>
                  <b>{severity.name}</b>
                  <small>{severity.detail}</small>
                </div>
                <div className="review-template-findings__severity-actions">
                  <button
                    type="button"
                    aria-label={`Edit ${severity.name} severity`}
                    onClick={() => openSeverityDialog(severity)}
                  >
                    <i className="bx bx-edit-alt" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${severity.name} severity`}
                    disabled={severityLevels.length === 1}
                    onClick={() =>
                      setPendingDeletion({
                        title: "Delete Severity Level",
                        itemName: severity.name,
                        description:
                          "This severity level will no longer be available for findings in this review template.",
                        confirmLabel: "Delete",
                        onConfirm: () =>
                          setSeverityLevels((current) =>
                            current.filter(
                              (item) => item.name !== severity.name
                            )
                          ),
                      })
                    }
                  >
                    <i className="bx bx-trash" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="review-template-findings__card review-template-findings__required">
          <header>
            <div>
              <h3>Required When Raising a Finding</h3>
              <p>
                Select the fields that must be completed when reviewers raise a
                finding.
              </p>
            </div>
          </header>
          <div>
            {[
              "Title",
              "Description",
              "Category",
              "Severity",
              "Recommendation",
              "Supporting Evidence",
              "Root Cause",
            ].map((field) => (
              <label key={field}>
                <Input
                  type="checkbox"
                  checked={requiredFindingFields.includes(field)}
                  onChange={() => toggleRequiredFindingField(field)}
                />
                {field}
              </label>
            ))}
          </div>
        </section>
        <aside className="review-template-findings__note">
          <i className="bx bx-info-circle" />
          <span>
            <b>
              These settings will apply to all engagements created using this
              review template.
            </b>{" "}
            You can change these settings anytime. Existing engagements will
            continue to follow the settings in effect.
          </span>
        </aside>
      </section>
      <aside className="review-template-findings__preview">
        <header>
          <h3>Preview (Finding Form)</h3>
          <p>This is how reviewers will raise a finding.</p>
        </header>
        <section>
          <h2>New Finding</h2>
          <label>
            Title {requiredFindingFields.includes("Title") && <b>*</b>}
            <Input placeholder="Enter finding title" readOnly />
          </label>
          <label>
            Category {requiredFindingFields.includes("Category") && <b>*</b>}
            <Input type="select" value="" readOnly>
              <option value="">Select category</option>
              {findingCategories.map((category) => (
                <option key={category}>{category}</option>
              ))}
            </Input>
          </label>
          <label>
            Severity {requiredFindingFields.includes("Severity") && <b>*</b>}
            <Input type="select" value="" readOnly>
              <option value="">Select severity</option>
              {severityLevels.map((severity) => (
                <option key={severity.name}>{severity.name}</option>
              ))}
            </Input>
          </label>
          <label>
            Description{" "}
            {requiredFindingFields.includes("Description") && <b>*</b>}
            <Input
              type="textarea"
              rows="4"
              placeholder="Describe the finding in detail..."
              readOnly
            />
          </label>
          <label>
            Recommendation{" "}
            {requiredFindingFields.includes("Recommendation") && <b>*</b>}
            <Input
              type="textarea"
              rows="4"
              placeholder="Provide your recommendation..."
              readOnly
            />
          </label>
          <aside>
            <i className="bx bx-info-circle" /> Actual form may vary based on
            engagement settings and user permissions.
          </aside>
        </section>
      </aside>
    </section>
  );
  const referencesView = (
    <section className="review-template-references">
      <section className="review-template-references__editor">
        <div className="review-template-references__heading">
          <i className="bx bx-link" />
          <div>
            <h2>References</h2>
            <p>
              Add the standards, guidance, policies and other references that
              apply to this review template.
            </p>
          </div>
          <Button type="button" onClick={openReferenceDrawer}>
            <i className="bx bx-plus" /> Add Reference
          </Button>
        </div>
        <section className="review-template-references__table">
          <header>
            <span>Title</span>
            <span>Type</span>
            <span>Reference / Version</span>
            <span>Source</span>
            <span>Actions</span>
          </header>
          {references.length ? (
            references.map((reference) => (
              <article
                key={reference.id}
                className={
                  selectedReferenceId === reference.id ? "is-selected" : ""
                }
                onClick={() => setSelectedReferenceId(reference.id)}
              >
                <i className="bx bx-grid-vertical" />
                <strong>{reference.title}</strong>
                <em
                  className={`review-template-references__type review-template-references__type--${reference.type
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  {reference.type}
                </em>
                <span>{reference.version}</span>
                <a
                  href={
                    reference.source === "external"
                      ? reference.value
                      : undefined
                  }
                  onClick={(event) => event.preventDefault()}
                >
                  <i
                    className={`bx ${
                      reference.source === "external" ? "bx-link" : "bx-file"
                    }`}
                  />{" "}
                  {reference.source === "external"
                    ? "External Link"
                    : reference.value}
                </a>
                <div>
                  <ActionMenu
                    label={`reference ${reference.title}`}
                    triggerClassName="review-template-references__more"
                    actions={[
                      {
                        label: "Preview reference",
                        icon: <i className="bx bx-show" />,
                        onSelect: () => setReferencePreview(reference),
                      },
                      {
                        label: "Edit reference",
                        icon: <i className="bx bx-pencil" />,
                        onSelect: () => editReference(reference),
                      },
                      {
                        label: "Delete reference",
                        icon: <i className="bx bx-trash" />,
                        className: "is-danger",
                        separatorBefore: true,
                        onSelect: () =>
                          setPendingDeletion({
                            title: "Delete Reference",
                            itemName: reference.title,
                            description:
                              "This reference will be removed from the review template.",
                            confirmLabel: "Delete",
                            onConfirm: () => {
                              setReferences((current) =>
                                current.filter(
                                  (item) => item.id !== reference.id
                                )
                              );
                              if (selectedReferenceId === reference.id)
                                setSelectedReferenceId("");
                            },
                          }),
                      },
                    ]}
                  />
                </div>
              </article>
            ))
          ) : (
            <aside className="review-template-references__empty">
              <i className="bx bx-link" />
              <h3>No references added</h3>
              <p>
                Add a standard, policy, guidance document, or external link.
              </p>
              <Button type="button" onClick={openReferenceDrawer}>
                <i className="bx bx-plus" /> Add Reference
              </Button>
            </aside>
          )}
        </section>
        <aside className="review-template-references__note">
          <i className="bx bx-info-circle" />
          <span>
            <b>About References</b> These references will be available to
            reviewers while performing the review. You can also link specific
            references to checklist questions.
          </span>
        </aside>
      </section>
      {isReferenceDrawerOpen && (
        <Offcanvas
          isOpen={isReferenceDrawerOpen}
          toggle={closeReferenceDrawer}
          direction="end"
          className="review-template-references__drawer"
          aria-label={editingReferenceId ? "Edit reference" : "Add reference"}
        >
          <div className="offcanvas-body">
            <section className="review-template-references__form">
              <header>
                <div>
                  <h2>
                    {editingReferenceId ? "Edit Reference" : "Add Reference"}
                  </h2>
                  <p>Provide the reference details for this review template.</p>
                </div>
                <button
                  type="button"
                  aria-label="Close reference drawer"
                  onClick={closeReferenceDrawer}
                >
                  <i className="bx bx-x" />
                </button>
              </header>
              <form onSubmit={addReference}>
                <label>
                  <span>
                    Title <b>*</b>
                  </span>
                  <Input
                    value={referenceForm.title}
                    invalid={Boolean(referenceErrors.title)}
                    aria-describedby={
                      referenceErrors.title
                        ? "reference-title-error"
                        : undefined
                    }
                    onChange={(event) =>
                      updateReferenceForm("title", event.target.value)
                    }
                    placeholder="e.g. AICPA Peer Review Program Manual"
                  />
                  {referenceErrors.title && (
                    <small
                      id="reference-title-error"
                      className="review-template-references__error"
                    >
                      {referenceErrors.title}
                    </small>
                  )}
                </label>
                <label>
                  <span>
                    Type <b>*</b>
                  </span>
                  <Input
                    type="select"
                    value={referenceForm.type}
                    invalid={Boolean(referenceErrors.type)}
                    aria-describedby={
                      referenceErrors.type ? "reference-type-error" : undefined
                    }
                    onChange={(event) =>
                      updateReferenceForm("type", event.target.value)
                    }
                  >
                    <option value="">Select type</option>
                    <option>Professional Standard</option>
                    <option>Firm Policy</option>
                    <option>Guidance</option>
                    <option>Other</option>
                  </Input>
                  {referenceErrors.type && (
                    <small
                      id="reference-type-error"
                      className="review-template-references__error"
                    >
                      {referenceErrors.type}
                    </small>
                  )}
                </label>
                <label>
                  <span>
                    Reference / Version <b>*</b>
                  </span>
                  <Input
                    value={referenceForm.version}
                    invalid={Boolean(referenceErrors.version)}
                    aria-describedby={
                      referenceErrors.version
                        ? "reference-version-error"
                        : undefined
                    }
                    onChange={(event) =>
                      updateReferenceForm("version", event.target.value)
                    }
                    placeholder="e.g. 2026 Edition"
                  />
                  {referenceErrors.version && (
                    <small
                      id="reference-version-error"
                      className="review-template-references__error"
                    >
                      {referenceErrors.version}
                    </small>
                  )}
                </label>
                <fieldset>
                  <legend>
                    Source <b>*</b>
                  </legend>
                  <div>
                    <Button
                      type="button"
                      className={
                        referenceForm.source === "external" ? "is-selected" : ""
                      }
                      onClick={() => setReferenceSource("external")}
                    >
                      <i className="bx bx-globe" /> External Link
                    </Button>
                    <Button
                      type="button"
                      className={
                        referenceForm.source === "document" ? "is-selected" : ""
                      }
                      onClick={() => setReferenceSource("document")}
                    >
                      <i className="bx bx-file" /> Document
                    </Button>
                  </div>
                </fieldset>
                {referenceForm.source === "external" ? (
                  <label>
                    <span>
                      URL <b>*</b>
                    </span>
                    <Input
                      value={referenceForm.value}
                      invalid={Boolean(referenceErrors.value)}
                      aria-describedby={
                        referenceErrors.value
                          ? "reference-value-error"
                          : undefined
                      }
                      onChange={(event) =>
                        updateReferenceForm("value", event.target.value)
                      }
                      placeholder="https://example.com"
                    />
                    {referenceErrors.value && (
                      <small
                        id="reference-value-error"
                        className="review-template-references__error"
                      >
                        {referenceErrors.value}
                      </small>
                    )}
                  </label>
                ) : (
                  <>
                    <section
                      className={`review-template-references__upload ${
                        referenceErrors.value ? "is-invalid" : ""
                      }`.trim()}
                    >
                      <i className="bx bx-cloud-upload" />
                      <div>
                        <b>{referenceForm.value || "Upload document"}</b>
                        <span>
                          {referenceForm.value
                            ? "Document selected"
                            : "Select the document to attach to this reference."}
                        </span>
                      </div>
                      <label className="btn btn-outline-primary btn-sm">
                        Browse files
                        <Input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                          onChange={selectReferenceDocument}
                        />
                      </label>
                    </section>
                    {referenceErrors.value && (
                      <small
                        id="reference-value-error"
                        className="review-template-references__error"
                      >
                        {referenceErrors.value}
                      </small>
                    )}
                  </>
                )}
                <label>
                  <span>
                    Description <small>(optional)</small>
                  </span>
                  <Input
                    type="textarea"
                    rows="4"
                    value={referenceForm.description}
                    onChange={(event) =>
                      updateReferenceForm("description", event.target.value)
                    }
                    placeholder="Brief description of the reference..."
                  />
                </label>
                <footer>
                  <Button type="button" onClick={closeReferenceDrawer}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingReferenceId ? "Save Changes" : "Add Reference"}
                  </Button>
                </footer>
              </form>
            </section>
          </div>
        </Offcanvas>
      )}
    </section>
  );
  const settingsView = (
    <section className="review-template-settings">
      <section className="review-template-settings__editor">
        <div className="review-template-settings__heading">
          <i className="bx bx-cog" />
          <div>
            <h2>Settings</h2>
            <p>
              Configure general behavior and rules for reviews created using
              this template.
            </p>
          </div>
        </div>
        <section className="review-template-settings__card">
          <h2>Review Behavior</h2>
          {settingToggle(
            "bx-check-square",
            "Require all mandatory questions before completion",
            "Review cannot be completed until all mandatory questions are answered.",
            "mandatoryQuestions"
          )}
          {settingToggle(
            "bx-git-compare",
            "Allow N/A answers",
            "Reviewers can mark questions as N/A where applicable.",
            "allowNa"
          )}
          {settingToggle(
            "bx-message-square-check",
            "Require completion comments",
            "Reviewers must provide comments when completing the review.",
            "completionComments"
          )}
          {settingToggle(
            "bx-lock-alt",
            "Lock review after completion",
            "Prevent further changes once the review is marked as complete.",
            "lockAfterCompletion"
          )}
        </section>
        <section className="review-template-settings__card">
          <h2>Checklist Behavior</h2>
          {settingToggle(
            "bx-list-plus",
            "Allow reviewers to add additional checklists",
            "Reviewers can add custom checklists during the review if needed.",
            "additionalChecklists"
          )}
          {settingToggle(
            "bx-recycle",
            "Allow reviewers to skip optional checklists",
            "Optional checklists can be skipped during the review.",
            "skipOptionalChecklists"
          )}
        </section>
        <section className="review-template-settings__card review-template-settings__naming">
          <h2>Review Naming</h2>
          <div>
            <span>
              <b>Default review name format</b>
              <small>This name will be used when creating a new review.</small>
            </span>
            <label>
              <Input
                type="select"
                value={templateSettings.namingFormat}
                onChange={(event) => {
                  setTemplateSettings((current) => ({
                    ...current,
                    namingFormat: event.target.value,
                  }));
                }}
              >
                <option>{"{Client} - {Year} Review"}</option>
                <option>{"{Client} - {Engagement} Review"}</option>
                <option>{"{Year} - {Client} Quality Review"}</option>
              </Input>
              <em>
                <b>Example:</b> ABC CPA LLC - 2026 Review
              </em>
            </label>
          </div>
        </section>
      </section>
      <aside className="review-template-settings__side">
        <aside className="review-template-settings__about">
          <i className="bx bx-info-circle" />
          <span>
            <b>About Settings</b> These settings apply to all reviews created
            using this template.
          </span>
        </aside>
        <section className="review-template-settings__preview">
          <h2>Review Experience Preview</h2>
          <p>
            These settings control how reviewers will experience the review.
          </p>
          <article>
            <span className="is-green">
              <i className="bx bx-check-square" />
            </span>
            <div>
              <h3>
                {templateSettings.mandatoryQuestions
                  ? "Structured Completion"
                  : "Flexible Completion"}
              </h3>
              <p>
                {templateSettings.mandatoryQuestions
                  ? "Reviewers must address all mandatory items before completing."
                  : "Reviewers can complete the review without every mandatory item."}
              </p>
            </div>
          </article>
          <article>
            <span className="is-blue">
              <i className="bx bx-message-square-dots" />
            </span>
            <div>
              <h3>
                {templateSettings.allowNa
                  ? "Flexible Responses"
                  : "Required Responses"}
              </h3>
              <p>
                {templateSettings.allowNa
                  ? "N/A options are available where applicable."
                  : "Questions must be answered without N/A responses."}
              </p>
            </div>
          </article>
          <article>
            <span className="is-orange">
              <i className="bx bx-list-check" />
            </span>
            <div>
              <h3>
                {templateSettings.additionalChecklists
                  ? "Open Checklists"
                  : "Controlled Checklists"}
              </h3>
              <p>
                {templateSettings.additionalChecklists
                  ? "Reviewers can add additional checklists as needed."
                  : "Reviewers follow the checklist structure defined by this template."}
              </p>
            </div>
          </article>
          <article>
            <span className="is-purple">
              <i className="bx bx-purchase-tag" />
            </span>
            <div>
              <h3>Consistent Naming</h3>
              <p>New reviews will follow the configured naming convention.</p>
            </div>
          </article>
        </section>
      </aside>
    </section>
  );

  const documentsView = (
    <section className="review-template-studio__information">
      <div className="review-template-studio__section-header">
        <span>
          <i className="bx bx-folder" />
        </span>
        <div>
          <h2>Documents &amp; Workpapers</h2>
          <p>
            This version's system folder blueprint is created for every
            engagement that uses the template.
          </p>
        </div>
      </div>
      <aside className="review-template-studio__info-note">
        <i className="bx bx-lock-alt" /> System Workpapers, report folders, and
        Archive are system-managed. Engagement users can create folders only
        within Client Documents.
      </aside>
      <div className="review-template-studio__document-folder-actions">
        <Button
          color="primary"
          type="button"
          disabled={isTemplatePublished}
          onClick={openDocumentFolderForm}
        >
          <i className="bx bx-folder-plus" /> Add Folder
        </Button>
      </div>
      <div className="review-template-studio__document-folders">
        {orderDocumentFolders(documentFolders).map((folder) => (
          <div
            key={folder.path}
            className={folder.isSystem ? "is-system" : "is-client"}
            style={{
              paddingLeft: `${18 + folder.path.split("/").length * 18}px`,
            }}
          >
            <i
              className={`bx ${folder.isSystem ? "bx-lock-alt" : "bx-folder"}`}
            />
            <strong>{folder.name}</strong>
            <small>{folder.isSystem ? "System-managed" : "User-managed"}</small>
          </div>
        ))}
      </div>
    </section>
  );

  const workflowView = (
    <section className="piers-workflow">
      <header className="piers-workflow__heading">
        <span>
          <i className="bx bx-git-branch" />
        </span>
        <div>
          <h2>Workflow</h2>
          <p>
            Configure checklist review assignments, sign-offs, completion rules,
            and final engagement approval.
          </p>
        </div>
      </header>
      <section
        className="piers-workflow__timeline"
        aria-label="Review workflow timeline"
      >
        <WorkflowTimelineStage
          number={1}
          tone="review"
          icon="bx-user-check"
          title="Engagement Preparation"
          label={
            engagementPreparationOwner || "Responsible role not configured"
          }
          description="Required before the engagement can move to Ready for Review."
          action={
            <Button
              outline
              type="button"
              onClick={openPreparationConfiguration}
            >
              Configure
            </Button>
          }
        >
          <div
            id="workflow-engagement-preparation"
            tabIndex="-1"
            className="piers-workflow__configuration-panel"
          >
            <div className="piers-workflow__responsible-role">
              <b>Responsible role</b>
              <strong
                className={engagementPreparationOwner ? "" : "is-incomplete"}
              >
                {engagementPreparationOwner || "Not configured"}
              </strong>
              {additionalAuthorizedRoles.length > 0 && (
                <span>
                  Also authorized: {additionalAuthorizedRoles.join(", ")}
                </span>
              )}
            </div>
            <div className="piers-workflow__panel-heading">
              <b>Required before review can start</b>
              <span>
                {readinessConfiguredCount} of{" "}
                {readinessRequirementOptions.length} configured
              </span>
            </div>
            <div className="piers-workflow__status-list">
              {readinessRequirementOptions.map(([key, label]) => (
                <span
                  className={
                    workflowConfiguration.readinessRequirements[key]
                      ? "is-complete"
                      : "is-incomplete"
                  }
                  key={key}
                >
                  <i
                    className={`bx ${
                      workflowConfiguration.readinessRequirements[key]
                        ? "bx-check-circle"
                        : "bx-error-circle"
                    }`}
                  />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </WorkflowTimelineStage>
        <WorkflowTimelineStage
          number={2}
          tone="system"
          icon="bx-cog"
          title="Ready for Review"
          label="System Gate"
          description="Confirms that the engagement has satisfied the prerequisites required to begin review."
        >
          <div className="piers-workflow__configuration-panel piers-workflow__system-validation">
            <div className="piers-workflow__panel-heading">
              <b>System validation</b>
              <span>{readinessConfiguredCount} readiness rules configured</span>
            </div>
            <div className="piers-workflow__status-list">
              {[
                [
                  "Engagement Preparation complete",
                  Boolean(engagementPreparationOwner) &&
                    readinessConfiguredCount ===
                      readinessRequirementOptions.length,
                ],
                [
                  "Required team assignments available",
                  workflowConfiguration.readinessRequirements
                    .reviewTeamAssigned &&
                    workflowConfiguration.readinessRequirements
                      .requiredRolesAssigned,
                ],
                [
                  "Required checklists configured",
                  workflowConfiguration.readinessRequirements
                    .requiredChecklistsAvailable &&
                    linkedChecklistIds.length > 0,
                ],
              ].map(([label, complete]) => (
                <span
                  className={complete ? "is-complete" : "is-incomplete"}
                  key={label}
                >
                  <i
                    className={`bx ${
                      complete ? "bx-check-circle" : "bx-error-circle"
                    }`}
                  />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </WorkflowTimelineStage>
        <WorkflowTimelineStage
          number={3}
          tone="review"
          icon="bx-list-check"
          title="Checklist Review"
          label="Review Assignments"
          description="Configure who performs each checklist review."
        >
          <div
            id="workflow-checklist-review"
            tabIndex="-1"
            className="piers-workflow__configuration-panel piers-workflow__table-panel"
          >
            {checklistWorkflowAssignments.length ? (
              <>
                <div className="piers-workflow__timeline-table piers-workflow__timeline-table--review">
                  <span>Checklist</span>
                  <span>Primary reviewer</span>
                  <span>Required</span>
                  <span>Action</span>
                </div>
                {checklistWorkflowAssignments.map(
                  ({ checklist, assignment }) => (
                    <div
                      className="piers-workflow__timeline-table piers-workflow__timeline-table--review is-row"
                      key={checklist.id}
                    >
                      <strong>{checklist.name}</strong>
                      <span>
                        {assignment?.primaryReviewerRole || (
                          <em>Not configured</em>
                        )}
                      </span>
                      <span>
                        {assignment?.required === false ? "No" : "Yes"}
                      </span>
                      <button
                        type="button"
                        onClick={() => openChecklistAssignment(checklist.id)}
                      >
                        {assignment ? "Configure" : "Configure review"}
                      </button>
                    </div>
                  )
                )}
                <footer>
                  <Button
                    outline
                    type="button"
                    disabled={!checklistWorkflowAssignments.length}
                    onClick={() =>
                      checklistWorkflowAssignments[0] &&
                      openChecklistAssignment(
                        checklistWorkflowAssignments[0].checklist.id
                      )
                    }
                  >
                    Configure Reviews
                  </Button>
                </footer>
              </>
            ) : (
              <aside className="piers-workflow__empty">
                <i className="bx bx-list-check" />
                <b>No linked checklists</b>
                <span>
                  Add checklists before configuring checklist review
                  assignments.
                </span>
              </aside>
            )}
          </div>
        </WorkflowTimelineStage>
        <WorkflowTimelineStage
          number={4}
          tone="approval"
          icon="bx-badge-check"
          title="Checklist Sign-off"
          label="Checklist Approvals"
          description="Configure who signs off each required checklist review."
        >
          <div className="piers-workflow__configuration-panel piers-workflow__table-panel">
            {checklistWorkflowAssignments.length ? (
              <>
                <div className="piers-workflow__timeline-table piers-workflow__timeline-table--signoff">
                  <span>Checklist</span>
                  <span>Sign-off role</span>
                  <span>Required</span>
                  <span>Action</span>
                </div>
                {checklistWorkflowAssignments.map(
                  ({ checklist, assignment }) => (
                    <div
                      className="piers-workflow__timeline-table piers-workflow__timeline-table--signoff is-row"
                      key={checklist.id}
                    >
                      <strong>{checklist.name}</strong>
                      <span>
                        {assignment?.signOffRole || <em>Not configured</em>}
                      </span>
                      <span>
                        {assignment?.required === false ? "No" : "Yes"}
                      </span>
                      <button
                        type="button"
                        onClick={() => openChecklistAssignment(checklist.id)}
                      >
                        {assignment ? "Configure" : "Configure sign-off"}
                      </button>
                    </div>
                  )
                )}
                <footer>
                  <Button
                    outline
                    type="button"
                    disabled={!checklistWorkflowAssignments.length}
                    onClick={() =>
                      checklistWorkflowAssignments[0] &&
                      openChecklistAssignment(
                        checklistWorkflowAssignments[0].checklist.id
                      )
                    }
                  >
                    Configure Sign-offs
                  </Button>
                </footer>
              </>
            ) : (
              <aside className="piers-workflow__empty">
                <i className="bx bx-badge-check" />
                <b>No linked checklists</b>
                <span>
                  Add checklists before configuring checklist sign-offs.
                </span>
              </aside>
            )}
          </div>
        </WorkflowTimelineStage>
        {hasAdditionalReview && (
          <WorkflowTimelineStage
            number={5}
            tone="review"
            icon="bx-layer"
            title="Additional Review"
            label="Additional Review Levels"
            description="Additional review levels configured for linked checklist reviews."
          >
            <div className="piers-workflow__configuration-panel piers-workflow__table-panel">
              <div className="piers-workflow__timeline-table piers-workflow__timeline-table--additional">
                <span>Checklist</span>
                <span>Review level</span>
                <span>Role</span>
                <span>Action</span>
              </div>
              {additionalReviewLevels.map((level, index) => (
                <div
                  className="piers-workflow__timeline-table piers-workflow__timeline-table--additional is-row"
                  key={`${level.checklist.id}-${level.name}-${index}`}
                >
                  <strong>{level.checklist.name}</strong>
                  <span>{level.name}</span>
                  <span>{level.role || <em>Not configured</em>}</span>
                  <button
                    type="button"
                    onClick={() => openChecklistAssignment(level.checklist.id)}
                  >
                    Configure
                  </button>
                </div>
              ))}
            </div>
          </WorkflowTimelineStage>
        )}
        <WorkflowTimelineStage
          number={hasAdditionalReview ? 6 : 5}
          tone="system"
          icon="bx-check-shield"
          title="Review Completion"
          label="System Gate"
          description="All review requirements must be satisfied before final engagement approval."
        >
          <div
            id="workflow-review-completion"
            tabIndex="-1"
            className="piers-workflow__configuration-panel piers-workflow__completion-panel"
          >
            <div className="piers-workflow__panel-heading">
              <b>Completion rules</b>
              <span>4 of 4 configured</span>
            </div>
            <div className="piers-workflow__status-list">
              {[
                "Required checklists and checklist sign-offs completed",
                "Required queries resolved and findings dispositioned",
                "Required conclusions complete and no blocking review items",
                "Required checklist reports generated",
              ].map((rule) => (
                <span className="is-complete" key={rule}>
                  <i className="bx bx-check-circle" />
                  {rule}
                </span>
              ))}
            </div>
            <div className="piers-workflow__completion-reports">
              <b>Reports generated at Review Completion</b>
              {completionReports.length ? (
                completionReports.map(({ report }) => (
                  <span key={report.id}>
                    <i className="bx bx-file" />
                    {report.name}
                  </span>
                ))
              ) : (
                <span>
                  No engagement-level reports are configured for this trigger.
                </span>
              )}
            </div>
          </div>
        </WorkflowTimelineStage>
        <WorkflowTimelineStage
          number={hasAdditionalReview ? 7 : 6}
          tone="approval"
          icon="bx-user-check"
          title="Final Engagement Sign-off"
          label="Final Approval"
          description="Configure the final approvers required before the engagement can be completed."
          action={
            <Button type="button" outline onClick={addFinalSignOff}>
              <i className="bx bx-plus" /> Add Sign-off Role
            </Button>
          }
        >
          <div
            id="workflow-final-engagement-sign-off"
            tabIndex="-1"
            className="piers-workflow__configuration-panel piers-workflow__approval-panel"
          >
            <div className="piers-workflow__panel-heading">
              <b>Final approval</b>
              <span>
                {workflowConfiguration.finalSignOffs.length} approval
                {workflowConfiguration.finalSignOffs.length === 1
                  ? ""
                  : "s"}{" "}
                required
              </span>
            </div>
            <div className="piers-workflow__signoffs">
              {workflowConfiguration.finalSignOffs.map((signOff, index) => (
                <article key={`${signOff.role}-${index}`}>
                  <b>{index + 1}</b>
                  <Input
                    type="select"
                    value={signOff.role}
                    onChange={(event) =>
                      updateFinalSignOff(index, event.target.value)
                    }
                  >
                    <option value="">Select role</option>
                    {workflowRoles.map((role) => (
                      <option key={role.id} value={role.name}>
                        {role.name}
                      </option>
                    ))}
                  </Input>
                  <span>Required</span>
                  <button
                    type="button"
                    aria-label="Move sign-off earlier"
                    disabled={index === 0}
                    onClick={() => moveFinalSignOff(index, -1)}
                  >
                    <i className="bx bx-up-arrow-alt" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move sign-off later"
                    disabled={
                      index === workflowConfiguration.finalSignOffs.length - 1
                    }
                    onClick={() => moveFinalSignOff(index, 1)}
                  >
                    <i className="bx bx-down-arrow-alt" />
                  </button>
                  <button
                    type="button"
                    aria-label="Remove sign-off"
                    onClick={() =>
                      setPendingDeletion({
                        title: "Remove Final Sign-off",
                        itemName: signOff.role || `Sign-off ${index + 1}`,
                        description:
                          "This role will no longer be required to sign off the final engagement.",
                        confirmLabel: "Remove",
                        onConfirm: () => removeFinalSignOff(index),
                      })
                    }
                  >
                    <i className="bx bx-trash" />
                  </button>
                </article>
              ))}
            </div>
          </div>
        </WorkflowTimelineStage>
        <WorkflowTimelineStage
          number={hasAdditionalReview ? 8 : 7}
          tone="completed"
          icon="bx-check-circle"
          title="Completed"
          label="System"
          description="Engagement review is complete after all required review and approval requirements have been satisfied."
          isLast
        />
      </section>
    </section>
  );

  const historyView = (
    <section className="review-template-history">
      <header>
        <span>
          <i className="bx bx-history" />
        </span>
        <div>
          <h2>Template History</h2>
          <p>Review the changes made to this template and its configuration.</p>
        </div>
      </header>
      <section className="review-template-history__timeline">
        {templateVersions.length ? (
          templateVersions.map((version) => (
            <article key={version.id}>
              <span
                className={
                  Number(version.id) === Number(currentTemplateVersion?.id)
                    ? "is-current"
                    : ""
                }
              >
                <i
                  className={`bx ${
                    version.isPublished ? "bx-check-circle" : "bx-edit-alt"
                  }`}
                />
              </span>
              <div>
                <p>
                  <b>
                    {version.isPublished
                      ? `Published ${formatPreviewVersion(version)}`
                      : `Draft ${formatPreviewVersion(version)}`}
                  </b>
                  <small>
                    {formatPreviewDate(version.updatedOn || version.createdOn)}
                  </small>
                </p>
                <strong>
                  {Number(version.id) === Number(currentTemplateVersion?.id)
                    ? "Current version"
                    : "Version history"}
                </strong>
                <em>
                  {version.isPublished
                    ? "Locked and available for engagement use."
                    : "Editable draft version."}
                </em>
              </div>
            </article>
          ))
        ) : (
          <p className="review-template-history__empty">
            No version history is available yet.
          </p>
        )}
      </section>
    </section>
  );

  const scrollPreviewTo = (target) => {
    setActivePreviewSection(target);
    document
      .getElementById(target)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const templatePreview = (
    <section className="review-template-preview" aria-label="Template preview">
      <section className="review-template-preview__title">
        <div>
          <h1 className="piers-page-title">
            {name} <small>{previewStatus}</small>
          </h1>
          <p>
            Version {previewVersion} <b>|</b> Last updated: {previewUpdatedOn}
          </p>
        </div>
        <div className="review-template-preview__title-actions">
          <Button
            type="button"
            outline
            className="review-template-preview__edit"
            onClick={editTemplateFromPreview}
          >
            <i className="bx bx-pencil" /> Edit
          </Button>
          <Button
            type="button"
            outline
            className="review-template-preview__duplicate"
            disabled={isDuplicating}
            onClick={duplicateTemplateFromPreview}
          >
            <i
              className={`bx ${
                isDuplicating ? "bx-loader-alt bx-spin" : "bx-copy"
              }`}
            />{" "}
            {isDuplicating ? "Duplicating..." : "Duplicate"}
          </Button>
          {!isTemplatePublished && (
            <Button
              type="button"
              className="review-template-preview__publish"
              onClick={publishTemplate}
            >
              <i className="bx bx-send" /> Publish Template
            </Button>
          )}
          <button
            type="button"
            className="review-template-preview__close"
            onClick={closeTemplatePreview}
            aria-label="Close template preview"
          >
            <i className="bx bx-x" />
          </button>
        </div>
      </section>
      <main className="piers-page">
        <aside className="review-template-preview__summary">
          <section>
            <h2>Template Summary</h2>
            {[
              ["bx-info-circle", "Information", null, "preview-information"],
              [
                "bx-check-square",
                "Checklists",
                checklistSections.length,
                "preview-checklists",
              ],
              [
                "bx-file",
                "Report Formats",
                linkedReports.length,
                "preview-reports",
              ],
              [
                "bx-flag",
                "Findings",
                findingCategories.length,
                "preview-findings",
              ],
              [
                "bx-folder",
                "Documents & Workpapers",
                documentFolders.length,
                "preview-documents",
              ],
              [
                "bx-git-branch",
                "Workflow",
                configuredWorkflowStages.length,
                "preview-workflow",
              ],
              [
                "bx-link",
                "References",
                references.length,
                "preview-references",
              ],
              ["bx-cog", "Review Settings", null, "preview-settings"],
            ].map(([icon, label, value, target]) => (
              <button
                type="button"
                key={label}
                className={activePreviewSection === target ? "is-active" : ""}
                onClick={() => scrollPreviewTo(target)}
              >
                <i className={`bx ${icon}`} />
                <b>{label}</b>
                {value !== null && <strong>{value}</strong>}
              </button>
            ))}
          </section>
          <section className="review-template-preview__about">
            <h2>About this Template</h2>
            <p>
              {description || "No description has been added to this template."}
            </p>
            <i className="bx bx-shield-quarter" aria-hidden="true" />
          </section>
        </aside>
        <section className="review-template-preview__primary">
          <section
            id="preview-information"
            className="review-template-preview__overview"
          >
            <h2>
              <span>1</span> Information
            </h2>
            <div className="review-template-preview__information-grid">
              <article className="review-template-preview__information-field review-template-preview__information-field--name">
                <span>Template Name</span>
                <b>{templateInformation.name || name || "Review Template"}</b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--code">
                <span>Template Code</span>
                <b>{templateInformation.code || "Not configured"}</b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--type">
                <span>Review Type</span>
                <b>
                  {reviewTypeOptions.find(
                    (type) =>
                      Number(type.id) === templateApplicability.reviewTypeId
                  )?.name ||
                    (templateApplicability.reviewTypeId
                      ? `Review type #${templateApplicability.reviewTypeId}`
                      : "Not configured")}
                </b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--owner">
                <span>Owner</span>
                <b>{templateInformation.owner || "Not configured"}</b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--effective-date">
                <span>Effective Date</span>
                <b>{templateInformation.effectiveDate || "Not configured"}</b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--version">
                <span>Version</span>
                <b>{previewVersion}</b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--status">
                <span>Status</span>
                <b>{previewStatus}</b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--tags">
                <span>Tags</span>
                <b>{templateInformation.tags.join(", ") || "No tags"}</b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--description">
                <span>Description</span>
                <b>{description || "No description has been added."}</b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--work-types">
                <span>Work Types</span>
                <b>
                  {applicabilityLabel(
                    workTypeOptions,
                    templateApplicability.workTypeIds,
                    "All work types"
                  )}
                </b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--industry-types">
                <span>Industry Types</span>
                <b>
                  {applicabilityLabel(
                    industryTypeOptions,
                    templateApplicability.industryTypeIds,
                    "All industry types"
                  )}
                </b>
              </article>
              <article className="review-template-preview__information-field review-template-preview__information-field--client-types">
                <span>Client Types</span>
                <b>
                  {applicabilityLabel(
                    clientTypeOptions,
                    templateApplicability.clientTypeIds,
                    "All client types"
                  )}
                </b>
              </article>
            </div>
          </section>
          <section
            id="preview-documents"
            className="review-template-preview__documents"
          >
            <h2>
              <span>5</span> Documents &amp; Workpapers (
              {documentFolders.length})
            </h2>
            <p>
              <i className="bx bx-lock-alt" /> System Workpapers and Archive are
              system-managed. Engagement users can add folders within Client
              Documents.
            </p>
            <div>
              {orderDocumentFolders(documentFolders).map((folder) => (
                <article
                  key={folder.path}
                  className={folder.isSystem ? "is-system" : "is-client"}
                  style={{
                    paddingLeft: `${20 + folder.path.split("/").length * 18}px`,
                  }}
                >
                  <i
                    className={`bx ${
                      folder.isSystem ? "bx-lock-alt" : "bx-folder"
                    }`}
                  />
                  <b>{folder.name}</b>
                  <small>
                    {folder.isSystem ? "System-managed" : "User-managed"}
                  </small>
                </article>
              ))}
            </div>
          </section>
          <section
            id="preview-workflow"
            className="review-template-preview__workflow"
          >
            <h2>
              <span>6</span> Workflow
            </h2>
            <div>
              {configuredWorkflowStages.map((stage, index) => (
                <React.Fragment key={stage.name}>
                  <article>
                    <span className="review-template-preview__workflow-icon">
                      <i
                        className={`bx ${
                          [
                            "bx-edit-alt",
                            "bx-user",
                            "bx-group",
                            "bx-shield",
                            "bx-check-circle",
                          ][index] || "bx-circle"
                        }`}
                      />
                    </span>
                    <div>
                      <b>{stage.name}</b>
                      <em>{stage.role || "Assigned reviewer"}</em>
                    </div>
                    <small>{stage.description}</small>
                  </article>
                  {index < configuredWorkflowStages.length - 1 && (
                    <span
                      className="review-template-preview__workflow-connector"
                      aria-hidden="true"
                    >
                      <i className="bx bx-right-arrow-alt" />
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
            <section
              className="review-template-preview__workflow-details"
              aria-label="Workflow step configuration"
            >
              <h3>Configured step details</h3>
              <ol>
                {workflowPreviewDetails.map((step, index) => (
                  <li key={step.title}>
                    <b>
                      <span>{index + 1}</span>
                      {step.title}
                    </b>
                    <strong>{step.summary}</strong>
                    <ul>
                      {step.details.map((detail, detailIndex) => (
                        <li key={`${step.title}-${detailIndex}`}>{detail}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            </section>
          </section>
          <section
            id="preview-checklists"
            className="review-template-preview__checklists"
          >
            <header>
              <h2>
                <span>2</span> Checklists ({checklistSections.length})
              </h2>
            </header>
            {checklistSections.map((section, index) => {
              const sectionChecklists = section.checklistIds
                .map((id) => checklistOptions.find((item) => item.id === id))
                .filter(Boolean);
              return (
                <article
                  key={section.id}
                  className="review-template-preview__checklist-section"
                >
                  <header>
                    <span className={`tone-${index}`}>
                      <i
                        className={`bx ${
                          [
                            "bx-calendar",
                            "bx-folder-open",
                            "bx-flag",
                            "bx-user",
                          ][index] || "bx-list-check"
                        }`}
                      />
                    </span>
                    <b>{section.name}</b>
                    <strong>{section.checklistIds.length}</strong>
                  </header>
                  <div className="review-template-preview__checklist-rows">
                    {sectionChecklists.length ? (
                      sectionChecklists.map((checklist) => (
                        <div
                          className="review-template-preview__checklist-row"
                          key={checklist.id}
                        >
                          <span>
                            <i className="bx bx-list-check" />
                          </span>
                          <div>
                            <b>{checklist.name}</b>
                            <small>
                              {checklist.description || "No description added."}
                            </small>
                          </div>
                          <button
                            type="button"
                            onClick={() => setPreviewChecklist(checklist)}
                          >
                            View details
                          </button>
                        </div>
                      ))
                    ) : (
                      <p>No checklists selected.</p>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
          <section
            id="preview-findings"
            className="review-template-preview__findings"
          >
            <h2>
              <span>4</span> Finding Configuration
            </h2>
            <div>
              <article>
                <b>Categories ({findingCategories.length})</b>
                <p>{findingCategories.join(", ")}</p>
              </article>
              <article>
                <b>Severity Levels ({severityLevels.length})</b>
                <div className="review-template-preview__severity">
                  {severityLevels.map((severity) => (
                    <span key={severity.name} className={severity.tone}>
                      <i />
                      {severity.name}
                    </span>
                  ))}
                </div>
              </article>
              <article>
                <b>Required When Raising a Finding</b>
                <div className="review-template-preview__required">
                  {requiredFindingFields.map((field) => (
                    <span key={field}>{field}</span>
                  ))}
                </div>
              </article>
            </div>
          </section>
          <section
            id="preview-reports"
            className="review-template-preview__reports"
          >
            <h2>
              <span>3</span> Report Formats ({linkedReports.length})
            </h2>
            {linkedReports.map(({ section, report }, index) => (
              <article key={report.id}>
                <strong>{index + 1}</strong>
                <div>
                  <b>{report.name}</b>
                  <small>{section}</small>
                </div>
                <span>{report.category || "Report Format"}</span>
                <em>
                  {reportTriggerText(
                    reportConfigurations[report.id] || defaultReportAvailability
                  )}
                </em>
              </article>
            ))}
          </section>
          <section
            id="preview-references"
            className="review-template-preview__references"
          >
            <h2>
              <span>7</span> References ({references.length})
            </h2>
            {references.map((reference) => (
              <article key={reference.id}>
                <i className="bx bx-file" />
                <div>
                  <b>{reference.title}</b>
                  <small>{reference.version || "No version specified"}</small>
                </div>
                <button type="button">
                  View <i className="bx bx-link-external" />
                </button>
              </article>
            ))}
          </section>
          <section
            id="preview-settings"
            className="review-template-preview__settings"
          >
            <h2>
              <span>8</span> Review Settings
            </h2>
            {[
              [
                "mandatoryQuestions",
                "Require mandatory questions before completion",
              ],
              ["allowNa", "Allow N/A responses"],
              ["completionComments", "Require completion comments"],
              ["lockAfterCompletion", "Lock review after completion"],
              [
                "additionalChecklists",
                "Allow reviewers to add additional checklists",
              ],
              [
                "skipOptionalChecklists",
                "Allow reviewers to skip optional checklists",
              ],
            ].map(([setting, label]) => (
              <article
                key={setting}
                className={templateSettings[setting] ? "is-on" : ""}
              >
                <i
                  className={`bx ${
                    templateSettings[setting]
                      ? "bx-check-circle"
                      : "bx-x-circle"
                  }`}
                />
                <div>
                  <b>{templateSettings[setting] ? "Enabled" : "Not enabled"}</b>
                  <span>{label}</span>
                </div>
              </article>
            ))}
          </section>
        </section>
      </main>
      <Modal
        fullscreen
        isOpen={Boolean(previewChecklist)}
        toggle={closeChecklistPreview}
        modalClassName="checklist-preview-modal-root"
        contentClassName="checklist-preview-modal"
        aria-label="Checklist preview"
      >
        <ChecklistPreview
          checklist={previewChecklist}
          checklistId={previewChecklist?.id}
          onClose={closeChecklistPreview}
          modal
        />
      </Modal>
    </section>
  );

  if (previewOnly)
    return (
      <Modal
        fullscreen
        isOpen={isTemplatePreviewOpen}
        toggle={closeTemplatePreview}
        backdrop="static"
        modalClassName="review-template-preview-modal"
        contentClassName="review-template-preview-modal__content"
        aria-label="Template preview"
      >
        <ModalBody className="p-0">{templatePreview}</ModalBody>
      </Modal>
    );

  return (
    <section
      className={`review-template-studio${
        isTemplatePublished ? " is-published" : ""
      }`}
      aria-label="Review template studio"
    >
      <header
        className="review-template-studio__header"
        data-template-tour="template-actions"
      >
        <h1 className="piers-page-title">{name || "New Review Template"}</h1>
        <div className="review-template-studio__top-actions">
          {template?.id && <Button
            type="button"
            className="review-template-studio__history"
            onClick={() => setIsHistoryDrawerOpen(true)}
          >
            <i className="bx bx-history" /> History
          </Button>}
          {template?.id && <Button
            type="button"
            className="review-template-studio__preview"
            data-template-tour="preview"
            onClick={() => setIsTemplatePreviewOpen(true)}
          >
            <i className="bx bx-show" /> Preview
          </Button>}
          {template?.id && !isTemplatePublished && (
            <Button
              type="button"
              className="review-template-studio__publish"
              onClick={publishTemplate}
            >
              Publish Template
            </Button>
          )}
          {isTemplatePublished && (
            <Button
              type="button"
              className="review-template-studio__publish"
              disabled={isManualSavePending}
              onClick={requestDraftVersion}
            >
              <i className="bx bx-copy-alt" /> Create Draft Version
            </Button>
          )}
          <Button
            type="button"
            className="review-template-studio__close-button"
            color="primary"
            outline
            onClick={closeAction}
            aria-label="Close template studio"
          >
            Close
          </Button>
        </div>
      </header>
      <main className="review-template-studio__body piers-page">
        <aside
          className="review-template-studio__tree"
          data-template-tour="outline"
        >
          <div className="review-template-studio__tree-heading">
            TEMPLATE OUTLINE
          </div>
          <div className="review-template-studio__branches">
            {outline.map(([id, icon, title, count, children]) => (
              <div key={id} className="review-template-studio__branch">
                <button
                  type="button"
                  className={active === id ? "is-active" : ""}
                  aria-current={active === id ? "step" : undefined}
                  disabled={isManualSavePending}
                  onClick={() => setActive(id)}
                >
                  <i className={`bx ${icon}`} />
                  <span>{title}</span>
                  {count && <b>{count}</b>}
                </button>
                {children && (
                  <div className="review-template-studio__children">
                    {children.map(([child, childCount, grandChildren]) => (
                      <div key={child}>
                        <button type="button" onClick={() => setActive(id)}>
                          <i className="bx bx-chevron-right" />
                          <span>{child}</span>
                          {childCount && <b>{childCount}</b>}
                        </button>
                        {grandChildren && (
                          <div className="review-template-studio__grandchildren">
                            {grandChildren.map((item) => (
                              <span key={item}>{item}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>
        <aside
          className="review-template-studio__tips review-template-studio__tree-guidance"
          aria-label="Template guidance and help"
        >
          <header>
            <span>
              <i className="bx bx-book-open" />
            </span>
            <div>
              <h2>Guidance</h2>
              <p>Help for this template section.</p>
            </div>
          </header>
          <div className="review-template-studio__guidance-list">
            {activeStepGuidance.map(([id, icon, title, detail]) => (
              <React.Fragment key={id}>
                <button
                  type="button"
                  className={displayedGuidanceId === id ? "is-active" : ""}
                  onClick={() => setExpandedGuidance(id)}
                >
                  <i className={`bx ${icon}`} />
                  <span>{title}</span>
                  <i className="bx bx-chevron-right" />
                </button>
                {displayedGuidanceId === id && <p>{detail}</p>}
              </React.Fragment>
            ))}
          </div>
          <section className="review-template-studio__guided-tour">
            <h3>
              <i className="bx bx-wink-smile" /> New to PIERS?
            </h3>
            <p>Take a quick guided tour to learn this template workspace.</p>
            <Button outline type="button" onClick={startTemplateTour}>
              <i className="bx bx-play" /> Start Guided Tour
            </Button>
          </section>
          <footer>
            <i className="bx bx-help-circle" /> Need more help? Open the Help
            icon in the top bar for contextual guidance.
          </footer>
        </aside>
        <section
          className={`review-template-studio__workspace ${
            (active === "checklists" && isChecklistLibraryOpen) ||
            (active === "reports" && isReportLibraryOpen)
              ? "has-checklist-library"
              : ""
          }`}
        >
          {isTemplatePublished && (
            <aside className="review-template-studio__published-notice">
              <i className="bx bx-lock-alt" /> This published version is
              read-only. Create a draft version to make changes.
            </aside>
          )}
          {active === "information" ? (
            <>
              <section
                className="review-template-studio__information"
                data-template-tour="information"
              >
                <div className="review-template-studio__section-header">
                  <span>
                    <i className="bx bx-info-circle" />
                  </span>
                  <div>
                    <h2>Information</h2>
                    <p>Basic details about this review template.</p>
                  </div>
                </div>
                <div className="review-template-studio__information-form">
                  <div className="review-template-studio__fields">
                    <label>
                      Template Name <b>*</b>
                      <Input
                        value={templateInformation.name}
                        invalid={informationSubmitted && !templateInformation.name.trim()}
                        aria-invalid={informationSubmitted && !templateInformation.name.trim()}
                        aria-describedby={informationSubmitted && !templateInformation.name.trim() ? "template-name-error" : undefined}
                        onChange={(event) =>
                          updateTemplateInformation("name", event.target.value)
                        }
                      />
                      {informationSubmitted && !templateInformation.name.trim() &&
                        <small id="template-name-error" className="review-template-studio__field-error">Template Name is required.</small>}
                    </label>
                    <label>
                      Template Code
                      <Input
                        value={templateInformation.code}
                        placeholder="Generated automatically if left blank"
                        onChange={(event) =>
                          updateTemplateInformation("code", event.target.value)
                        }
                      />
                    </label>
                    <label>
                      Review Type <b>*</b>
                      <Input
                        type="select"
                        value={templateApplicability.reviewTypeId ?? ""}
                        invalid={informationSubmitted && !templateApplicability.reviewTypeId}
                        aria-invalid={informationSubmitted && !templateApplicability.reviewTypeId}
                        aria-describedby={informationSubmitted && !templateApplicability.reviewTypeId ? "review-type-error" : undefined}
                        onChange={(event) =>
                          setTemplateApplicability((current) => ({
                            ...current,
                            reviewTypeId: event.target.value
                              ? Number(event.target.value)
                              : null,
                          }))
                        }
                      >
                        <option value="" disabled>
                          Select review type
                        </option>
                        {templateApplicability.reviewTypeId &&
                          !reviewTypeOptions.some(
                            (type) =>
                              Number(type.id) ===
                              templateApplicability.reviewTypeId
                          ) && (
                            <option
                              value={templateApplicability.reviewTypeId}
                              disabled
                            >
                              Unavailable review type #
                              {templateApplicability.reviewTypeId}
                            </option>
                          )}
                        {reviewTypeOptions
                          .filter(
                            (type) =>
                              Number(type.status) === 1 ||
                              Number(type.id) ===
                                templateApplicability.reviewTypeId
                          )
                          .map((type) => (
                            <option
                              key={type.id}
                              value={type.id}
                              disabled={Number(type.status) !== 1}
                            >
                              {type.name}
                              {Number(type.status) !== 1 ? " (inactive)" : ""}
                            </option>
                          ))}
                      </Input>
                      {informationSubmitted && !templateApplicability.reviewTypeId &&
                        <small id="review-type-error" className="review-template-studio__field-error">Review Type is required.</small>}
                    </label>
                    <label className="review-template-studio__description">
                      Description
                      <Input
                        type="textarea"
                        rows="4"
                        value={templateInformation.description}
                        onChange={(event) =>
                          updateTemplateInformation(
                            "description",
                            event.target.value
                          )
                        }
                      />
                    </label>
                    <label className="review-template-studio__hidden-detail">
                      Owner <b>*</b>
                      <Input
                        value={templateInformation.owner}
                        onChange={(event) =>
                          updateTemplateInformation("owner", event.target.value)
                        }
                      />
                    </label>
                    <label className="review-template-studio__hidden-detail">
                      Effective Date <b>*</b>
                      <Input
                        value={templateInformation.effectiveDate}
                        onChange={(event) =>
                          updateTemplateInformation(
                            "effectiveDate",
                            event.target.value
                          )
                        }
                      />
                    </label>
                    <div className="review-template-studio__applicability-fields">
                      <label className="review-template-studio__applicability">
                        Work Types <b>*</b>
                        <Select
                          classNamePrefix="review-template-applicability-select"
                          isMulti
                          isSearchable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          onChange={(selected) =>
                            setTemplateApplicability((current) => ({
                              ...current,
                              workTypeIds: (selected || []).map(
                                (option) => option.value
                              ),
                            }))
                          }
                          options={workTypeOptions}
                          placeholder="Select work types"
                          className={informationSubmitted && !templateApplicability.workTypeIds.length ? "review-template-studio__invalid-select" : undefined}
                          aria-describedby={informationSubmitted && !templateApplicability.workTypeIds.length ? "work-types-error" : undefined}
                          aria-invalid={informationSubmitted && !templateApplicability.workTypeIds.length}
                          styles={applicabilitySelectStyles}
                          value={workTypeOptions.filter((option) =>
                            templateApplicability.workTypeIds.includes(
                              option.value
                            )
                          )}
                        />
                        {informationSubmitted && !templateApplicability.workTypeIds.length &&
                          <small id="work-types-error" className="review-template-studio__field-error">Select at least one Work Type.</small>}
                      </label>
                      <label className="review-template-studio__applicability">
                        Industry Types <b>*</b>
                        <Select
                          classNamePrefix="review-template-applicability-select"
                          isMulti
                          isSearchable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          onChange={(selected) =>
                            setTemplateApplicability((current) => ({
                              ...current,
                              industryTypeIds: (selected || []).map(
                                (option) => option.value
                              ),
                            }))
                          }
                          options={industryTypeOptions}
                          placeholder="Select industry types"
                          className={informationSubmitted && !templateApplicability.industryTypeIds.length ? "review-template-studio__invalid-select" : undefined}
                          aria-describedby={informationSubmitted && !templateApplicability.industryTypeIds.length ? "industry-types-error" : undefined}
                          aria-invalid={informationSubmitted && !templateApplicability.industryTypeIds.length}
                          styles={applicabilitySelectStyles}
                          value={industryTypeOptions.filter((option) =>
                            templateApplicability.industryTypeIds.includes(
                              option.value
                            )
                          )}
                        />
                        {informationSubmitted && !templateApplicability.industryTypeIds.length &&
                          <small id="industry-types-error" className="review-template-studio__field-error">Select at least one Industry Type.</small>}
                      </label>
                      <label className="review-template-studio__applicability review-template-studio__hidden-detail">
                        Client Types
                        <Select
                          classNamePrefix="review-template-applicability-select"
                          isMulti
                          isSearchable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          onChange={(selected) =>
                            setTemplateApplicability((current) => ({
                              ...current,
                              clientTypeIds: (selected || []).map(
                                (option) => option.value
                              ),
                            }))
                          }
                          options={clientTypeOptions}
                          placeholder="All client types"
                          styles={applicabilitySelectStyles}
                          value={clientTypeOptions.filter((option) =>
                            templateApplicability.clientTypeIds.includes(
                              option.value
                            )
                          )}
                        />
                      </label>
                    </div>
                    <label className="review-template-studio__tags">
                      Tags
                      <CreatableSelect
                        isMulti
                        inputValue={tagInput}
                        onInputChange={(value, meta) => { if (meta.action === "input-change") setTagInput(value); }}
                        value={templateInformation.tags.map((tag) => ({ value: tag, label: tag }))}
                        options={[]}
                        menuIsOpen={false}
                        onChange={(selected) => updateTemplateInformation("tags", normalizeTags((selected || []).map((option) => option.value)))}
                        onKeyDown={(event) => {
                          if (["Enter", ",", "Tab"].includes(event.key) && tagInput.trim()) {
                            if (event.key !== "Tab") event.preventDefault();
                            updateTemplateInformation("tags", normalizeTags([...templateInformation.tags, tagInput]));
                            setTagInput("");
                          }
                        }}
                        onBlur={() => {
                          if (tagInput.trim()) updateTemplateInformation("tags", normalizeTags([...templateInformation.tags, tagInput]));
                          setTagInput("");
                        }}
                        placeholder="Type a tag and press Enter or comma"
                        styles={applicabilitySelectStyles}
                      />
                    </label>
                  </div>
                </div>
              </section>
            </>
          ) : active === "workflow" ? (
            workflowView
          ) : active === "checklists" ? (
            checklistsView
          ) : active === "reports" ? (
            reportsView
          ) : active === "findings" ? (
            findingsView
          ) : active === "documents" ? (
            documentsView
          ) : active === "references" ? (
            referencesView
          ) : active === "settings" ? (
            settingsView
          ) : active === "workflow" ? (
            <section className="review-template-workflow">
              <section className="review-template-workflow__editor">
                <div className="review-template-workflow__heading">
                  <span>
                    <i className="bx bx-git-branch" />
                  </span>
                  <div>
                    <h2>Workflow</h2>
                    <p>Define the stages of the review process.</p>
                  </div>
                </div>
                <div className="review-template-workflow__stage-header">
                  <div>
                    <h3>Review Stages</h3>
                    <p>
                      Add and arrange the stages in the order the review will be
                      completed.
                    </p>
                  </div>
                  <Button
                    type="button"
                    className="review-template-workflow__add"
                    onClick={openAddStage}
                  >
                    <i className="bx bx-plus" /> Add Stage
                  </Button>
                </div>
                <div className="review-template-workflow__stages">
                  {stages.map((stage, index) => (
                    <article
                      key={`${stage.name}-${index}`}
                      className="review-template-workflow__stage"
                    >
                      <i className="bx bx-grid-vertical review-template-workflow__drag" />
                      <span
                        className={`review-template-workflow__number review-template-workflow__number--${stage.tone}`}
                      >
                        {index + 1}
                      </span>
                      <div className="review-template-workflow__stage-copy">
                        <h4>{stage.name}</h4>
                        <p>{stage.description}</p>
                      </div>
                      <div className="review-template-workflow__role">
                        <i className="bx bx-user" />
                        <span>
                          Assigned Role<b>{stage.role}</b>
                        </span>
                      </div>
                      <div className="review-template-workflow__stage-actions">
                        <button
                          type="button"
                          className="review-template-workflow__stage-action"
                          aria-label={`Edit ${stage.name} stage`}
                          title="Edit stage"
                          onClick={() => openEditStage(index)}
                        >
                          <i className="bx bx-pencil" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="review-template-workflow__stage-action review-template-workflow__stage-action--delete"
                          aria-label={`Delete ${stage.name} stage`}
                          title="Delete stage"
                          disabled={
                            stage.stageType === "system" || stages.length === 1
                          }
                          onClick={() => setStageToDelete(index)}
                        >
                          <i className="bx bx-trash" aria-hidden="true" />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
                <aside className="review-template-workflow__help">
                  <i className="bx bx-info-circle" />
                  <div>
                    <b>How it works</b>
                    <span>
                      The review will move from one stage to the next in this
                      order.
                    </span>
                  </div>
                </aside>
              </section>
              <aside className="review-template-workflow__summary">
                <div className="review-template-workflow__summary-heading">
                  <span>
                    <i className="bx bx-line-chart" />
                  </span>
                  <h3>Workflow Summary</h3>
                </div>
                <dl>
                  <div>
                    <dt>Total Stages</dt>
                    <dd>{stages.length}</dd>
                  </div>
                  <div>
                    <dt>First Stage</dt>
                    <dd>{stages[0]?.name}</dd>
                  </div>
                  <div>
                    <dt>Last Stage</dt>
                    <dd>{stages.at(-1)?.name}</dd>
                  </div>
                  <div className="review-template-workflow__completion-stage">
                    <dt>Completion Stage</dt>
                    <dd>Completed</dd>
                  </div>
                </dl>
                <section className="review-template-workflow__flow">
                  <h3>Stage Flow</h3>
                  {stages.map((stage, index) => (
                    <React.Fragment key={`${stage.name}-flow-${index}`}>
                      <div
                        className={`review-template-workflow__flow-stage review-template-workflow__flow-stage--${stage.tone}`}
                      >
                        <b>{index + 1}</b>
                        <span>{stage.name}</span>
                      </div>
                      {index < stages.length - 1 && (
                        <i className="bx bx-down-arrow-alt" />
                      )}
                    </React.Fragment>
                  ))}
                </section>
                <Button
                  type="button"
                  className="review-template-workflow__preview"
                  onClick={() => setIsWorkflowPreviewOpen(true)}
                >
                  <i className="bx bx-show" /> Preview Workflow
                </Button>
              </aside>
            </section>
          ) : (
            <section className="review-template-studio__empty">
              <i className="bx bx-cog" />
              <h2>Configure {outline.find(([id]) => id === active)?.[2]}</h2>
              <p>Add details to grow this template's structure.</p>
              <Button className="review-template-studio__publish">
                Add Details
              </Button>
            </section>
          )}
        </section>
      </main>
      <footer className="review-template-studio__modal-footer">
        <div>
          {previousOutline && (
            <Button
              outline
              type="button"
              onClick={() => setActive(previousOutline[0])}
            >
              <i className="bx bx-left-arrow-alt" />
              Previous
            </Button>
          )}
          <small>
            <i
              className={`bx ${
                autoSaveStatus === "error"
                  ? "bx-error-circle"
                  : "bx-check-circle"
              }`}
            />
            {!template?.id && autoSaveStatus !== "saving"
              ? "Save to create this template"
              : autoSaveStatus === "error"
              ? "Changes not saved"
              : autoSaveStatus === "saving"
              ? "Saving changes..."
              : "Changes saved automatically"}
          </small>
        </div>
        <div>
          {isTemplatePublished ? (
            <Button
              color="primary"
              type="button"
              disabled={isManualSavePending}
              onClick={requestDraftVersion}
            >
              <i className="bx bx-copy-alt" />
              Create Draft Version
            </Button>
          ) : (
            <Button
              outline
              type="button"
              disabled={isManualSavePending}
              onClick={saveTemplate}
            >
              <i
                className={`bx ${
                  isManualSavePending ? "bx-loader-alt bx-spin" : "bx-save"
                }`}
              />
              {isManualSavePending
                ? "Saving…"
                : active === "settings"
                ? "Save Changes"
                : "Save"}
            </Button>
          )}
          {active === "settings" && !isTemplatePublished && (
            <Button
              color="primary"
              type="button"
              disabled={isManualSavePending}
              onClick={publishTemplate}
            >
              <i className="bx bx-send" /> Save & Publish
            </Button>
          )}
          {nextOutline && (
            <Button
              color="primary"
              type="button"
              disabled={isManualSavePending}
              onClick={async () => {
                if (isTemplatePublished || await saveTemplate()) setActive(nextOutline[0]);
              }}
            >
              Next: {nextOutline[2]} <i className="bx bx-right-arrow-alt" />
            </Button>
          )}
        </div>
      </footer>
      <Offcanvas
        isOpen={isHistoryDrawerOpen}
        toggle={() => setIsHistoryDrawerOpen(false)}
        direction="end"
        className="review-template-history-drawer"
        aria-label="Template history"
      >
        <header className="review-template-history-drawer__header">
          <button
            type="button"
            onClick={() => setIsHistoryDrawerOpen(false)}
            aria-label="Close history"
          >
            <i className="bx bx-x" />
          </button>
        </header>
        <div className="review-template-history-drawer__content">
          {historyView}
        </div>
      </Offcanvas>
      <Offcanvas
        isOpen={assignmentDialog !== null}
        toggle={() => setAssignmentDialog(null)}
        direction="end"
        className="piers-workflow-assignment"
        aria-label="Configure checklist review"
      >
        <div className="offcanvas-body">
          {assignmentForm && (
            <form onSubmit={saveChecklistAssignment}>
              <header>
                <div>
                  <h2>Configure Checklist Review</h2>
                  <p>
                    {
                      checklistOptions.find(
                        (item) => item.id === assignmentForm.checklistId
                      )?.name
                    }
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setAssignmentDialog(null)}
                  aria-label="Close"
                >
                  <i className="bx bx-x" />
                </button>
              </header>
              <label>
                <span>
                  Primary Reviewer <b>*</b>
                </span>
                <Input
                  type="select"
                  value={assignmentForm.primaryReviewerRole}
                  onChange={(event) =>
                    updateAssignmentForm(
                      "primaryReviewerRole",
                      event.target.value
                    )
                  }
                >
                  <option value="">Select role</option>
                  {workflowRoles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </Input>
              </label>
              <label>
                Sign-off Role
                <Input
                  type="select"
                  value={assignmentForm.signOffRole}
                  onChange={(event) =>
                    updateAssignmentForm("signOffRole", event.target.value)
                  }
                >
                  <option value="">No sign-off required</option>
                  {workflowRoles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </Input>
              </label>
              <section className="piers-workflow-assignment__levels">
                <header>
                  <b>Additional Review Levels</b>
                  <button type="button" onClick={addAssignmentReviewLevel}>
                    <i className="bx bx-plus" /> Add level
                  </button>
                </header>
                {assignmentForm.reviewLevels.map((level, index) => (
                  <div key={`${level.name}-${index}`}>
                    <Input
                      value={level.name}
                      onChange={(event) =>
                        updateAssignmentReviewLevel(
                          index,
                          "name",
                          event.target.value
                        )
                      }
                      placeholder="Level name"
                    />
                    <Input
                      type="select"
                      value={level.role}
                      onChange={(event) =>
                        updateAssignmentReviewLevel(
                          index,
                          "role",
                          event.target.value
                        )
                      }
                    >
                      <option value="">Select role</option>
                      {workflowRoles.map((role) => (
                        <option key={role.id} value={role.name}>
                          {role.name}
                        </option>
                      ))}
                    </Input>
                    <button
                      type="button"
                      aria-label="Remove review level"
                      onClick={() =>
                        setPendingDeletion({
                          title: "Remove Review Level",
                          itemName: level.name || `Review level ${index + 1}`,
                          description:
                            "This additional review level will be removed from the checklist assignment.",
                          confirmLabel: "Remove",
                          onConfirm: () => removeAssignmentReviewLevel(index),
                        })
                      }
                    >
                      <i className="bx bx-trash" />
                    </button>
                  </div>
                ))}
              </section>
              <label>
                Checklist Report
                <Input
                  type="select"
                  value={assignmentForm.reportTemplateId || ""}
                  onChange={(event) =>
                    updateAssignmentForm(
                      "reportTemplateId",
                      event.target.value ? Number(event.target.value) : null
                    )
                  }
                >
                  <option value="">No linked report</option>
                  {linkedReports
                    .filter(
                      ({ report }) =>
                        (
                          reportConfigurations[report.id] ||
                          reportDefaults(report.id)
                        ).scope === "checklist"
                    )
                    .map(({ report }) => (
                      <option key={report.id} value={report.id}>
                        {report.name}
                      </option>
                    ))}
                </Input>
              </label>
              <div className="form-check piers-workflow-assignment__required">
                <Input
                  id={`checklist-required-${assignmentForm.checklistId}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={assignmentForm.required}
                  onChange={(event) =>
                    updateAssignmentForm("required", event.target.checked)
                  }
                />
                <Label
                  className="form-check-label"
                  htmlFor={`checklist-required-${assignmentForm.checklistId}`}
                >
                  Required checklist
                </Label>
              </div>
              <footer className="piers-workflow-drawer__footer">
                <Button
                  outline
                  type="button"
                  className="piers-workflow-drawer__cancel"
                  onClick={() => setAssignmentDialog(null)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="piers-workflow-drawer__save">
                  Save Assignment
                </Button>
              </footer>
            </form>
          )}
        </div>
      </Offcanvas>
      <Offcanvas
        isOpen={isPreparationDialogOpen}
        toggle={() => setIsPreparationDialogOpen(false)}
        direction="end"
        className="piers-workflow-preparation"
        aria-label="Configure Engagement Preparation"
      >
        <div className="offcanvas-body">
          {preparationForm && (
            <form onSubmit={savePreparationConfiguration}>
              <header>
                <div>
                  <h2>Engagement Preparation Configuration</h2>
                  <p>
                    Configure who owns preparation and the requirements for
                    moving to Ready for Review.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPreparationDialogOpen(false)}
                  aria-label="Close"
                >
                  <i className="bx bx-x" />
                </button>
              </header>
              <label>
                <span>
                  Responsible Role <b>*</b>
                </span>
                <Input
                  type="select"
                  value={preparationForm.responsibleRoleId}
                  disabled={!workflowRoles.length}
                  onChange={(event) =>
                    setPreparationForm((current) => ({
                      ...current,
                      responsibleRoleId: event.target.value,
                    }))
                  }
                >
                  <option value="">
                    {workflowRoles.length
                      ? "Select role"
                      : "No eligible engagement roles are available"}
                  </option>
                  {workflowRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Input>
                {!workflowRoles.length && (
                  <small>No eligible engagement roles are available.</small>
                )}
              </label>
              <section className="piers-workflow-preparation__authorized">
                <b>Additional roles allowed to complete preparation</b>
                <span>
                  Optional. The responsible role remains the primary owner.
                </span>
                {workflowRoles
                  .filter(
                    (role) =>
                      role.id !== Number(preparationForm.responsibleRoleId)
                  )
                  .map((role) => (
                    <label key={role.id}>
                      <Input
                        type="checkbox"
                        checked={preparationForm.additionalAuthorizedRoleIds.includes(
                          role.id
                        )}
                        onChange={() =>
                          togglePreparationAuthorizedRole(role.id)
                        }
                      />
                      {role.name}
                    </label>
                  ))}
              </section>
              <section className="piers-workflow-preparation__requirements">
                <b>Required Readiness Checks</b>
                {[
                  [
                    "engagementInformationComplete",
                    "Engagement information complete",
                  ],
                  ["reviewTeamAssigned", "Review team assigned"],
                  ["requiredRolesAssigned", "Required roles assigned"],
                  [
                    "requiredChecklistsAvailable",
                    "Required checklists available",
                  ],
                  [
                    "requiredEngagementDataAvailable",
                    "Required engagement data available",
                  ],
                  [
                    "requiredDocumentsAvailable",
                    "Required documents/workpapers available",
                  ],
                ].map(([key, label]) => (
                  <label key={key}>
                    <Input
                      type="checkbox"
                      checked={preparationForm.readinessRequirements[key]}
                      onChange={() =>
                        setPreparationForm((current) => ({
                          ...current,
                          readinessRequirements: {
                            ...current.readinessRequirements,
                            [key]: !current.readinessRequirements[key],
                          },
                        }))
                      }
                    />
                    {label}
                  </label>
                ))}
              </section>
              <footer className="piers-workflow-drawer__footer">
                <Button
                  outline
                  type="button"
                  className="piers-workflow-drawer__cancel"
                  onClick={() => setIsPreparationDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="piers-workflow-drawer__save">
                  Save
                </Button>
              </footer>
            </form>
          )}
        </div>
      </Offcanvas>
      <Modal
        isOpen={isFindingCategoryModalOpen}
        toggle={() => setIsFindingCategoryModalOpen(false)}
        centered
        className="review-template-finding-category"
        contentClassName="review-template-finding-category__content"
      >
        <ModalBody>
          <form onSubmit={addFindingCategory}>
            <header>
              <div>
                <h2>Add Finding Category</h2>
                <p>
                  Create a category reviewers can select when raising a finding.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close finding category"
                onClick={() => setIsFindingCategoryModalOpen(false)}
              >
                <i className="bx bx-x" />
              </button>
            </header>
            <label>
              Category name
              <Input
                autoFocus
                value={newFindingCategory}
                onChange={(event) => setNewFindingCategory(event.target.value)}
                placeholder="e.g. Engagement Quality"
              />
            </label>
            <footer>
              <Button
                type="button"
                onClick={() => setIsFindingCategoryModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!newFindingCategory.trim()}>
                Add Category
              </Button>
            </footer>
          </form>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={Boolean(severityForm)}
        toggle={() => setSeverityForm(null)}
        centered
        className="review-template-finding-category"
        contentClassName="review-template-finding-category__content"
      >
        <ModalBody>
          {severityForm && (
            <form onSubmit={saveSeverityLevel}>
              <header>
                <div>
                  <h2>
                    {severityForm.originalName
                      ? "Edit Severity"
                      : "Add Severity"}
                  </h2>
                  <p>
                    Define the severity level reviewers can assign to findings.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close severity"
                  onClick={() => setSeverityForm(null)}
                >
                  <i className="bx bx-x" />
                </button>
              </header>
              <label>
                Severity name
                <Input
                  autoFocus
                  value={severityForm.name}
                  onChange={(event) =>
                    setSeverityForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="e.g. Critical"
                />
              </label>
              <label>
                Description
                <Input
                  value={severityForm.detail}
                  onChange={(event) =>
                    setSeverityForm((current) => ({
                      ...current,
                      detail: event.target.value,
                    }))
                  }
                  placeholder="e.g. Highest risk / impact"
                />
              </label>
              <label>
                Indicator color
                <Input
                  type="select"
                  value={severityForm.tone}
                  onChange={(event) =>
                    setSeverityForm((current) => ({
                      ...current,
                      tone: event.target.value,
                    }))
                  }
                >
                  <option value="critical">Red</option>
                  <option value="high">Orange</option>
                  <option value="medium">Yellow</option>
                  <option value="low">Green</option>
                </Input>
              </label>
              <footer>
                <Button type="button" onClick={() => setSeverityForm(null)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !severityForm.name.trim() || !severityForm.detail.trim()
                  }
                >
                  {severityForm.originalName ? "Save Changes" : "Add Severity"}
                </Button>
              </footer>
            </form>
          )}
        </ModalBody>
      </Modal>
      <Offcanvas
        isOpen={isCreateSectionOpen}
        toggle={closeChecklistSectionDrawer}
        direction="end"
        className="review-template-create-section"
        aria-label={
          editingChecklistSection
            ? "Edit checklist section"
            : "Create checklist section"
        }
      >
        <div className="offcanvas-body">
          <form onSubmit={saveChecklistSection}>
            <header>
              <div>
                <h2>
                  {editingChecklistSection ? "Edit Section" : "Create Section"}
                </h2>
                <p>
                  {editingChecklistSection
                    ? "Update the name of this checklist section."
                    : "Create a checklist section, then link existing checklists to it."}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close checklist section"
                onClick={closeChecklistSectionDrawer}
              >
                <i className="bx bx-x" />
              </button>
            </header>
            <label>
              <span>
                Section name <b aria-hidden="true">*</b>
              </span>
              <Input
                autoFocus
                required
                value={newSectionName}
                onChange={(event) => setNewSectionName(event.target.value)}
                placeholder="e.g. Quality review"
              />
            </label>
            <footer>
              <Button type="button" onClick={closeChecklistSectionDrawer}>
                Cancel
              </Button>
              <Button type="submit" disabled={!newSectionName.trim()}>
                {editingChecklistSection
                  ? "Save Changes"
                  : "Create & Link Checklists"}
              </Button>
            </footer>
          </form>
        </div>
      </Offcanvas>
      <Modal
        isOpen={isWorkflowPreviewOpen}
        toggle={() => setIsWorkflowPreviewOpen(false)}
        centered
        className="review-template-workflow-preview"
        contentClassName="review-template-workflow-preview__content"
        aria-label="Preview Workflow"
      >
        <ModalBody>
          <header className="review-template-workflow-preview__header">
            <div>
              <h2>Preview Workflow</h2>
              <p>This is how the review will progress from start to finish.</p>
            </div>
            <button
              type="button"
              onClick={() => setIsWorkflowPreviewOpen(false)}
              aria-label="Close workflow preview"
            >
              <i className="bx bx-x" />
            </button>
          </header>
          <section className="review-template-workflow-preview__stages">
            {stages.map((stage, index) => (
              <React.Fragment key={`${stage.name}-preview-${index}`}>
                <article className="review-template-workflow-preview__stage">
                  <span
                    className={`review-template-workflow__number review-template-workflow__number--${stage.tone}`}
                  >
                    {index + 1}
                  </span>
                  <div>
                    <h3>{stage.name}</h3>
                    <p>{stage.description}</p>
                  </div>
                  {index === 0 && (
                    <b className="review-template-workflow-preview__current">
                      Current
                    </b>
                  )}
                </article>
                {index < stages.length - 1 && (
                  <i className="bx bx-down-arrow-alt review-template-workflow-preview__arrow" />
                )}
              </React.Fragment>
            ))}
          </section>
          <aside className="review-template-workflow-preview__note">
            <i className="bx bx-info-circle" />
            <span>
              This is a linear workflow. Each stage must be completed before
              moving to the next.
            </span>
          </aside>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={Boolean(stageDialog)}
        toggle={closeStageDialog}
        centered
        className="review-template-stage-dialog"
        contentClassName="review-template-stage-dialog__content"
        aria-label={stageDialog?.mode === "edit" ? "Edit stage" : "Add stage"}
      >
        <ModalBody>
          <form onSubmit={saveStage}>
            <header>
              <div>
                <h2>
                  {stageDialog?.mode === "edit" ? "Edit Stage" : "Add Stage"}
                </h2>
                <p>
                  {stageDialog?.mode === "edit"
                    ? "Update the details for this review stage."
                    : "Define the details for the new review stage."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeStageDialog}
                aria-label="Close stage form"
              >
                <i className="bx bx-x" />
              </button>
            </header>
            <Label for="stage-name">
              <span>
                Stage Name <b>*</b>
              </span>
              <Input
                id="stage-name"
                autoFocus
                value={stageForm.name}
                onChange={(event) =>
                  updateStageForm("name", event.target.value)
                }
                placeholder="Enter stage name"
              />
            </Label>
            <Label for="stage-description">
              <span>Description</span>
              <Input
                id="stage-description"
                type="textarea"
                rows="3"
                maxLength="200"
                value={stageForm.description}
                onChange={(event) =>
                  updateStageForm("description", event.target.value)
                }
                placeholder="Enter stage description (optional)"
              />
              <small>{stageForm.description.length} / 200</small>
            </Label>
            <Label for="stage-role">
              <span>
                Assigned Role <b>*</b>
              </span>
              <Input
                id="stage-role"
                type="select"
                value={
                  stageForm.stageType === "system" ? "System" : stageForm.role
                }
                disabled={stageForm.stageType === "system"}
                onChange={(event) =>
                  updateStageForm("role", event.target.value)
                }
              >
                {stageRoles.map((role) => (
                  <option key={role}>{role}</option>
                ))}
              </Input>
            </Label>
            <fieldset>
              <legend>Stage Type</legend>
              <label>
                <Input
                  type="radio"
                  name="stageType"
                  value="manual"
                  checked={stageForm.stageType === "manual"}
                  onChange={(event) =>
                    updateStageForm("stageType", event.target.value)
                  }
                />{" "}
                Manual
              </label>
              <label>
                <Input
                  type="radio"
                  name="stageType"
                  value="system"
                  checked={stageForm.stageType === "system"}
                  onChange={(event) =>
                    updateStageForm("stageType", event.target.value)
                  }
                />{" "}
                System
              </label>
            </fieldset>
            <label className="review-template-stage-dialog__optional">
              <Input
                type="checkbox"
                checked={stageForm.optional}
                onChange={(event) =>
                  updateStageForm("optional", event.target.checked)
                }
              />{" "}
              Allow optional (stage can be skipped)
            </label>
            <footer>
              <Button type="button" outline onClick={closeStageDialog}>
                Cancel
              </Button>
              <Button type="submit">
                {stageDialog?.mode === "edit" ? "Update Stage" : "Add Stage"}
              </Button>
            </footer>
          </form>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={Boolean(documentFolderForm)}
        toggle={() => setDocumentFolderForm(null)}
        centered
        className="review-template-document-folder-modal"
        aria-label="Add template folder"
      >
        <ModalBody>
          <form onSubmit={addTemplateDocumentFolder}>
            <header>
              <span>
                <i className="bx bx-folder-plus" />
              </span>
              <div>
                <h2>Add Folder</h2>
                <p>
                  This folder will be created for every new engagement that uses
                  this template version.
                </p>
              </div>
            </header>
            <Label for="template-document-folder-name">Folder name</Label>
            <Input
              id="template-document-folder-name"
              autoFocus
              maxLength="80"
              value={documentFolderForm?.name || ""}
              onChange={(event) =>
                setDocumentFolderForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              placeholder="e.g. Specialist Work"
            />
            <Label className="mt-3" for="template-document-folder-parent">
              Create inside
            </Label>
            <Input
              id="template-document-folder-parent"
              type="select"
              value={documentFolderForm?.parentPath || ""}
              onChange={(event) =>
                setDocumentFolderForm((current) => ({
                  ...current,
                  parentPath: event.target.value,
                }))
              }
            >
              {documentFolders
                .filter((folder) => !folder.isSystem)
                .map((folder) => (
                  <option key={folder.path} value={folder.path}>
                    {"  ".repeat(folder.path.split("/").length - 1)}
                    {folder.name}
                  </option>
                ))}
            </Input>
            <footer>
              <Button
                type="button"
                outline
                onClick={() => setDocumentFolderForm(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={!documentFolderForm?.name?.trim()}
              >
                <i className="bx bx-folder-plus" /> Add Folder
              </Button>
            </footer>
          </form>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={Boolean(publishReadinessIssues)}
        toggle={() => setPublishReadinessIssues(null)}
        centered
        className="review-template-stage-delete review-template-publish-readiness"
        aria-label="Template cannot be published"
      >
        <ModalBody>
          <header>
            <span>
              <i className="bx bx-error-alt" />
            </span>
            <div>
              <h2>Template is not ready to publish</h2>
              <p>
                Resolve the following issues, then publish the template again.
              </p>
            </div>
          </header>
          <ul>
            {(publishReadinessIssues || []).map((issue, index) => (
              <li key={`${issue}-${index}`}>{issue}</li>
            ))}
          </ul>
          <footer>
            <Button
              type="button"
              color="primary"
              onClick={() => setPublishReadinessIssues(null)}
            >
              Continue Editing
            </Button>
          </footer>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={isDraftVersionConfirmationOpen}
        toggle={() => setIsDraftVersionConfirmationOpen(false)}
        centered
        className="review-template-stage-delete review-template-version-confirm"
        aria-label="Create draft version"
      >
        <ModalBody>
          <header>
            <span>
              <i className="bx bx-git-branch" />
            </span>
            <div>
              <h2>Create draft version</h2>
              <p>
                Create draft v
                {Number(currentTemplateVersion?.versionNumber || 0) + 1}.0 from
                published v{formatPreviewVersion(currentTemplateVersion)}?
              </p>
            </div>
          </header>
          <p>
            Existing engagements will continue using v
            {formatPreviewVersion(currentTemplateVersion)}. You can edit the
            draft without changing the published version.
          </p>
          <footer>
            <Button
              type="button"
              outline
              onClick={() => setIsDraftVersionConfirmationOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" color="primary" onClick={confirmDraftVersion}>
              Create draft
            </Button>
          </footer>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={Boolean(pendingDeletion)}
        toggle={() => setPendingDeletion(null)}
        centered
        className="review-template-stage-delete"
        aria-label={pendingDeletion?.title || "Confirm deletion"}
      >
        <ModalBody>
          <header>
            <span>
              <i className="bx bx-error-alt" />
            </span>
            <div>
              <h2>{pendingDeletion?.title || "Confirm deletion"}</h2>
              <p>
                Are you sure you want to{" "}
                {pendingDeletion?.confirmLabel?.toLowerCase() || "delete"}{" "}
                <b>{pendingDeletion?.itemName}</b>?
              </p>
            </div>
          </header>
          <p>
            {pendingDeletion?.description || "This action cannot be undone."}
          </p>
          <footer>
            <Button
              type="button"
              outline
              onClick={() => setPendingDeletion(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              color="danger"
              onClick={confirmPendingDeletion}
            >
              {pendingDeletion?.confirmLabel || "Delete"}
            </Button>
          </footer>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={stageToDelete !== null}
        toggle={() => setStageToDelete(null)}
        centered
        className="review-template-stage-delete"
        aria-label="Delete stage"
      >
        <ModalBody>
          <header>
            <span>
              <i className="bx bx-error-alt" />
            </span>
            <div>
              <h2>Delete Stage</h2>
              <p>
                Are you sure you want to delete{" "}
                <b>{stages[stageToDelete]?.name}</b>?
              </p>
            </div>
          </header>
          <p>This action cannot be undone.</p>
          <footer>
            <Button
              type="button"
              outline
              onClick={() => setStageToDelete(null)}
            >
              Cancel
            </Button>
            <Button type="button" color="danger" onClick={deleteStage}>
              Delete
            </Button>
          </footer>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={Boolean(referencePreview)}
        toggle={() => setReferencePreview(null)}
        centered
        className="review-template-reference-preview"
        aria-label="Reference preview"
      >
        <ModalBody>
          {referencePreview && (
            <section className="review-template-references__preview">
              <header>
                <h2>Reference Preview</h2>
                <button
                  type="button"
                  aria-label="Close reference preview"
                  onClick={() => setReferencePreview(null)}
                >
                  <i className="bx bx-x" />
                </button>
              </header>
              <article>
                <span>
                  <i className="bx bx-book-bookmark" />
                </span>
                <div>
                  <h3>{referencePreview.title}</h3>
                  <p>
                    <em
                      className={`review-template-references__type review-template-references__type--${referencePreview.type
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      {referencePreview.type}
                    </em>
                    <b>{referencePreview.version}</b>
                  </p>
                  <a
                    href={
                      referencePreview.source === "external"
                        ? referencePreview.value
                        : undefined
                    }
                    onClick={(event) => event.preventDefault()}
                  >
                    {referencePreview.value}{" "}
                    <i className="bx bx-link-external" />
                  </a>
                  <small>
                    {referencePreview.description || "No description provided."}
                  </small>
                </div>
              </article>
            </section>
          )}
        </ModalBody>
      </Modal>
      <Modal
        fullscreen
        isOpen={isTemplatePreviewOpen}
        toggle={closeTemplatePreview}
        backdrop="static"
        modalClassName="review-template-preview-modal"
        contentClassName="review-template-preview-modal__content"
        aria-label="Template preview"
      >
        <ModalBody className="p-0">{templatePreview}</ModalBody>
      </Modal>
    </section>
  );
};
export default ReviewTemplateStudio;
